import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Row,
  Col,
  Tabs,
  Tab,
  Table,
  InputGroup
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../Shared/Navbar/Navbar';
import { baseurl } from '../../Api/Baseurl';
import { Pencil, Trash } from 'react-bootstrap-icons';

const AddHoneyMoonTour = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get tour ID from URL for edit mode
  const isEditMode = !!id;

  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dropdowns
  const [categories, setCategories] = useState([]);
  const [destinations, setDestinations] = useState([]);

  // Add state for tourist visa remarks (free flow field)
  const [touristVisaRemarks, setTouristVisaRemarks] = useState('');

  // ========================
  // UNIFIED EDITING STATE (matches INTLAddTour)
  // ========================
  const [editingItem, setEditingItem] = useState(null);
  const [editingType, setEditingType] = useState('');
  const [editIndex, setEditIndex] = useState(-1);

  // Visa form editing state
  const [editingVisaItemId, setEditingVisaItemId] = useState(null);
  const [editingVisaFormIndex, setEditingVisaFormIndex] = useState(null);
  const [visaFormEditData, setVisaFormEditData] = useState({
    type: '',
    download_action: '',
    fill_action: '',
    action1_file: null,
    action2_file: null
  });

  // Add this function to get file URL
  const getFileUrl = (fileName) => {
    if (!fileName || typeof fileName !== 'string') {
      return null;
    }

    if (fileName.startsWith('http')) {
      return fileName;
    }

    if (fileName.startsWith('/uploads/')) {
      return `${baseurl}${fileName}`;
    }

    return `${baseurl}/uploads/visa/${fileName}`;
  };

  // Function to open file in new tab
  const openFileInNewTab = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  // BASIC DETAILS
  const [formData, setFormData] = useState({
    tour_code: '',
    tour_type: "honeymoon",
    title: '',
    category_id: 1,
    primary_destination_id: '',
    country_id: '', // Add this line
    duration_days: '',
    overview: '',
    base_price_adult: '',
    emi_price: '', // ← Add this line
    is_international: 1,
    cost_remarks: "",
    hotel_remarks: "",
    transport_remarks: "",
    booking_poi_remarks: "",
    cancellation_remarks: "",
    emi_remarks: "",
    optional_tour_remarks: "" // ← ADD THIS LINE
  });

  // TAB ORDER MUST MATCH JSX ORDER
  const visaSubTabs = ['tourist', 'transit', 'business', 'form', 'photo', 'fees', 'submission'];

  const TAB_LIST = [
    'basic',
    'itineraries',
    'departures',
    'costs',
    'optionalTours',
    'emiOptions',
    'inclusions',
    'exclusions',
    'transport',
    'hotels',
    ...(formData.is_international === 1 ? ['visa'] : []), // Main visa tab
    'bookingPoi',
    'cancellation',
    'instructions',
    'images'
  ];

  // DEPARTURES (multiple)
  const [departureForm, setDepartureForm] = useState({
    departure_date: '',
    return_date: '',
    adult_price: '',
    child_price: '',
    infant_price: '',
    description: '',
    total_seats: ''
  });
  const [departures, setDepartures] = useState([]);

  // EXCLUSIONS
  const [exclusionText, setExclusionText] = useState('');
  const [exclusions, setExclusions] = useState([]);

  // INCLUSIONS
  const [inclusionText, setInclusionText] = useState('');
  const [inclusions, setInclusions] = useState([]);

  // IMAGES
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imageCaption, setImageCaption] = useState('');

  // Add these for image management:
  const [existingImages, setExistingImages] = useState([]);
  const [editingImageId, setEditingImageId] = useState(null);
  const [replacementFile, setReplacementFile] = useState(null);
  const [replacementPreview, setReplacementPreview] = useState(null);

  const [freeFlowPhotoEntries, setFreeFlowPhotoEntries] = useState([]);
  const [freeFlowPhotoText, setFreeFlowPhotoText] = useState('');

  // Add Free Flow Photo Entry
  const addFreeFlowPhotoEntry = () => {
    const trimmed = freeFlowPhotoText.trim();
    if (!trimmed) return;

    const newItem = { description: trimmed };

    if (editingType === 'freeFlowPhoto' && editIndex !== -1) {
      const updated = [...freeFlowPhotoEntries];
      updated[editIndex] = newItem;
      setFreeFlowPhotoEntries(updated);
    } else {
      setFreeFlowPhotoEntries(prev => [...prev, newItem]);
    }

    setFreeFlowPhotoText('');
    resetEditing();
  };

  // Edit Free Flow Photo Entry
  const editFreeFlowPhotoEntry = (idx) => {
    const item = freeFlowPhotoEntries[idx];
    setFreeFlowPhotoText(item.description);
    setEditingItem(item);
    setEditingType('freeFlowPhoto');
    setEditIndex(idx);
    setEditingVisaItemId(idx);
  };

  // Remove Free Flow Photo Entry
  const removeFreeFlowPhotoEntry = (idx) => {
    setFreeFlowPhotoEntries(prev => prev.filter((_, i) => i !== idx));
    if (editingType === 'freeFlowPhoto' && editIndex === idx) {
      resetEditing();
    }
  };

  // Handle Free Flow Photo Text Change
  const handleFreeFlowPhotoChange = (e) => {
    setFreeFlowPhotoText(e.target.value);
  };

  // =======================
  // TOUR COST
  // =======================
  const [tourCostItem, setTourCostItem] = useState({
    pax: '',
    standard_hotel: '',
    deluxe_hotel: '',
    executive_hotel: '',
    child_with_bed: '',
    child_no_bed: '',
    remarks: ''
  });
  const [tourCosts, setTourCosts] = useState([]);

  const handleCostChange = (e) => {
    const { name, value } = e.target;
    setTourCostItem(prev => ({ ...prev, [name]: value }));
  };

  const addCostRow = () => {
    if (!tourCostItem.pax) return;

    if (editingType === 'cost' && editIndex !== -1) {
      // Update existing
      const updated = [...tourCosts];
      updated[editIndex] = { ...tourCostItem };
      setTourCosts(updated);
    } else {
      // Add new
      setTourCosts(prev => [...prev, { ...tourCostItem }]);
    }

    setTourCostItem({
      pax: '',
      standard_hotel: '',
      deluxe_hotel: '',
      executive_hotel: '',
      child_with_bed: '',
      child_no_bed: '',
      remarks: ''
    });
    resetEditing();
  };

  const editCostRow = (idx) => {
    const item = tourCosts[idx];
    setTourCostItem(item);
    setEditingItem(item);
    setEditingType('cost');
    setEditIndex(idx);
  };

  const removeCostRow = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this cost row?');
    if (confirmDelete) {
      setTourCosts(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'cost' && editIndex === idx) {
        resetEditing();
      }
    }
  };

  // =======================
  // OPTIONAL TOURS
  // =======================
  const [optionalTourItem, setOptionalTourItem] = useState({
    tour_name: '',
    adult_price: '',
    child_price: ''
  });
  const [optionalTours, setOptionalTours] = useState([]);

  const handleOptionalTourChange = (e) => {
    const { name, value } = e.target;
    setOptionalTourItem(prev => ({ ...prev, [name]: value }));
  };

  const addOptionalTourRow = () => {
    if (!optionalTourItem.tour_name.trim()) return;

    const processedItem = {
      ...optionalTourItem
    };

    if (editingType === 'optionalTour' && editIndex !== -1) {
      const updated = [...optionalTours];
      updated[editIndex] = processedItem;
      setOptionalTours(updated);
    } else {
      setOptionalTours(prev => [...prev, processedItem]);
    }

    setOptionalTourItem({
      tour_name: '',
      adult_price: '',
      child_price: ''
    });
    resetEditing();
  };

  const editOptionalTourRow = (idx) => {
    const item = optionalTours[idx];
    setOptionalTourItem(item);
    setEditingItem(item);
    setEditingType('optionalTour');
    setEditIndex(idx);
  };

  const removeOptionalTourRow = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this optional tour?');
    if (confirmDelete) {
      setOptionalTours(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'optionalTour' && editIndex === idx) {
        resetEditing();
      }
    }
  };

  // ========================
  // RESET EDITING CONTEXT (unified, matches INTLAddTour)
  // ========================
  const resetEditing = () => {
    setEditingItem(null);
    setEditingType('');
    setEditIndex(-1);
    setEditingVisaItemId(null);
    setEditingVisaFormIndex(null);

    // Also reset any form fields that might be in edit mode
    if (editingType === 'touristVisa') {
      setTouristVisaForm({ description: '' });
    } else if (editingType === 'transitVisa') {
      setTransitVisaForm({ description: '' });
    } else if (editingType === 'businessVisa') {
      setBusinessVisaForm({ description: '' });
    } else if (editingType === 'photo') {
      setPhotoForm({ description: '' });
    } else if (editingType === 'freeFlowPhoto') {
      setFreeFlowPhotoText('');
    }

    // Reset visa form edit data
    if (editingVisaFormIndex !== null) {
      setVisaFormEditData({
        type: '',
        download_action: '',
        fill_action: '',
        action1_file: null,
        action2_file: null
      });
      setEditingVisaFormIndex(null);
    }
  };

  // =======================
  // EMI OPTIONS
  // =======================
  const [emiOptions, setEmiOptions] = useState([
    { particulars: 'Per Month Payment', loan_amount: '', months: 6, emi: '' },
    { particulars: 'Per Month Payment', loan_amount: '', months: 12, emi: '' },
    { particulars: 'Per Month Payment', loan_amount: '', months: 18, emi: '' },
    { particulars: 'Per Month Payment', loan_amount: '', months: 24, emi: '' },
    { particulars: 'Per Month Payment', loan_amount: '', months: 30, emi: '' },
    { particulars: 'Per Month Payment', loan_amount: '', months: 36, emi: '' },
    { particulars: 'Per Month Payment', loan_amount: '', months: 48, emi: '' }
  ]);
  // Add this state for EMI
  const [emiLoanAmount, setEmiLoanAmount] = useState('');
  const [emiInterestRate, setEmiInterestRate] = useState(18);

  // Add this function to calculate EMI
  const calculateEMI = (loanAmount, months, interestRate = 18) => {
    const principal = parseFloat(loanAmount);
    const monthlyRate = (interestRate / 100) / 12;
    const n = parseInt(months, 10);

    if (isNaN(principal) || principal <= 0 || isNaN(n) || n <= 0) return 0;

    const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, n) /
      (Math.pow(1 + monthlyRate, n) - 1);

    return Math.round(emi * 100) / 100;
  };

  // Add this effect to update EMI values when loan amount changes
  useEffect(() => {
    if (emiLoanAmount && !isNaN(emiLoanAmount) && emiLoanAmount > 0) {
      const updatedOptions = emiOptions.map(option => ({
        ...option,
        loan_amount: emiLoanAmount,
        emi: calculateEMI(emiLoanAmount, option.months, emiInterestRate)
      }));
      setEmiOptions(updatedOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emiLoanAmount, emiInterestRate]);

  // Update the handleLoanAmountChange function
  const handleLoanAmountChange = (value) => {
    setEmiLoanAmount(value);

    if (value && !isNaN(value) && value > 0) {
      const updatedOptions = emiOptions.map(option => ({
        ...option,
        loan_amount: value,
        emi: calculateEMI(value, option.months, emiInterestRate)
      }));
      setEmiOptions(updatedOptions);
    }
  };

  // =======================
  // HOTELS
  // =======================
  const [hotelItem, setHotelItem] = useState({
    city: '',
    nights: '',
    standard_hotel_name: '',
    deluxe_hotel_name: '',
    executive_hotel_name: '',
    remarks: ''
  });
  const [hotelRows, setHotelRows] = useState([]);

  const handleHotelChange = (e) => {
    const { name, value } = e.target;
    setHotelItem(prev => ({ ...prev, [name]: value }));
  };

  const addHotelRow = () => {
    if (!hotelItem.city.trim() ||
      (!hotelItem.standard_hotel_name.trim() &&
        !hotelItem.deluxe_hotel_name.trim() &&
        !hotelItem.executive_hotel_name.trim())) {
      setError('Please enter city and at least one hotel name');
      return;
    }

    if (editingType === 'hotel' && editIndex !== -1) {
      // Update existing item
      const updated = [...hotelRows];
      updated[editIndex] = { ...hotelItem };
      setHotelRows(updated);
    } else {
      // Add new item
      setHotelRows(prev => [...prev, { ...hotelItem }]);
    }

    // Reset form
    setHotelItem({
      city: '',
      nights: '',
      standard_hotel_name: '',
      deluxe_hotel_name: '',
      executive_hotel_name: '',
      remarks: ''
    });

    // Reset editing context
    resetEditing();
  };

  // Edit hotel row
  const editHotelRow = (idx) => {
    const item = hotelRows[idx];
    setHotelItem(item);
    setEditingItem(item);
    setEditingType('hotel');
    setEditIndex(idx);
  };

  const removeHotelRow = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this hotel?');
    if (confirmDelete) {
      setHotelRows(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'hotel' && editIndex === idx) {
        resetEditing();
      }
    }
  };

  // ========================
  // VISA STATE FOR Honey Moon TOURS
  // ========================
  const [activeVisaSubTab, setActiveVisaSubTab] = useState('tourist');

  // Tourist Visa
  const [touristVisaItems, setTouristVisaItems] = useState([]);
  const [touristVisaForm, setTouristVisaForm] = useState({ description: '' });

  // Transit Visa
  const [transitVisaItems, setTransitVisaItems] = useState([]);
  const [transitVisaForm, setTransitVisaForm] = useState({ description: '' });

  // Business Visa
  const [businessVisaItems, setBusinessVisaItems] = useState([]);
  const [businessVisaForm, setBusinessVisaForm] = useState({ description: '' });

  // Visa Form
  const [visaFormItems, setVisaFormItems] = useState([]);

  // Photo
  const [photoItems, setPhotoItems] = useState([]);
  const [photoForm, setPhotoForm] = useState({ description: '' });

  // Visa Fees
  const [visaFeesRows, setVisaFeesRows] = useState([
    {
      id: 1,
      type: 'Visa Fee',
      tourist: '',
      transit: '',
      business: '',
      tourist_charges: '', // Separate for Tourist
      transit_charges: '', // Separate for Transit
      business_charges: '' // Separate for Business
    },
    {
      id: 2,
      type: 'VFS Fee',
      tourist: '',
      transit: '',
      business: '',
      tourist_charges: '',
      transit_charges: '',
      business_charges: ''
    },
    {
      id: 3,
      type: 'Other Charges',
      tourist: '',
      transit: '',
      business: '',
      tourist_charges: '',
      transit_charges: '',
      business_charges: ''
    }
  ]);

  // Function to add new free flow entry
  const addVisaFeesRow = () => {
    const newId = visaFeesRows.length > 0
      ? Math.max(...visaFeesRows.map(row => row.id)) + 1
      : 1;

    setVisaFeesRows([
      ...visaFeesRows,
      {
        id: newId,
        type: 'Free Flow Entry',
        tourist: '',
        transit: '',
        business: '',
        tourist_charges: '',
        transit_charges: '',
        business_charges: ''
      }
    ]);
  };

  // Submission & Pick Up
  const [submissionRows, setSubmissionRows] = useState([
    {
      id: 1,
      label: 'Passport Submission Day',
      tourist: '',
      transit: '',
      business: ''
    },
    {
      id: 2,
      label: 'Passport Submission Time',
      tourist: '',
      transit: '',
      business: ''
    },
    {
      id: 3,
      label: 'Passport pick up Days',
      tourist: '',
      transit: '',
      business: ''
    },
    {
      id: 4,
      label: 'Passport Pick Up Time',
      tourist: '',
      transit: '',
      business: ''
    },
    {
      id: 5,
      label: 'Biometric requirement',
      tourist: '',
      transit: '',
      business: ''
    }
  ]);

  // Function to add new free flow entry in Submission & Pick Up
  const addSubmissionRow = () => {
    const newId = submissionRows.length > 0
      ? Math.max(...submissionRows.map(row => row.id)) + 1
      : 1;

    setSubmissionRows([
      ...submissionRows,
      {
        id: newId,
        label: 'Free Flow Entry',
        tourist: '',
        transit: '',
        business: ''
      }
    ]);
  };

  // Function to handle label change in Submission & Pick Up
  const handleSubmissionLabelChange = (id, value) => {
    const updated = submissionRows.map(row =>
      row.id === id ? { ...row, label: value } : row
    );
    setSubmissionRows(updated);
  };

  // Function to handle value change in Submission & Pick Up
  const handleSubmissionValueChange = (id, field, value) => {
    const updated = submissionRows.map(row =>
      row.id === id ? { ...row, [field]: value } : row
    );
    setSubmissionRows(updated);
  };

  // Edit Tourist Visa
  const editTouristVisa = (idx) => {
    const item = touristVisaItems[idx];
    setTouristVisaForm({ description: item.description });
    setEditingItem(item);
    setEditingType('touristVisa');
    setEditIndex(idx);
    setEditingVisaItemId(idx);
  };

  // Edit Transit Visa
  const editTransitVisa = (idx) => {
    const item = transitVisaItems[idx];
    setTransitVisaForm({ description: item.description });
    setEditingItem(item);
    setEditingType('transitVisa');
    setEditIndex(idx);
    setEditingVisaItemId(idx);
  };

  // Edit Business Visa
  const editBusinessVisa = (idx) => {
    const item = businessVisaItems[idx];
    setBusinessVisaForm({ description: item.description });
    setEditingItem(item);
    setEditingType('businessVisa');
    setEditIndex(idx);
    setEditingVisaItemId(idx);
  };

  // Edit Photo
  const editPhoto = (idx) => {
    const item = photoItems[idx];
    setPhotoForm({ description: item.description });
    setEditingItem(item);
    setEditingType('photo');
    setEditIndex(idx);
    setEditingVisaItemId(idx);
  };

  // Edit Visa Form Item
  const editVisaFormItem = (index) => {
    const formItem = visaFormItems[index];
    setVisaFormEditData({
      type: formItem.type,
      download_action: formItem.download_action,
      fill_action: formItem.fill_action,
      action1_file: formItem.action1_file,
      action2_file: formItem.action2_file
    });
    setEditingVisaFormIndex(index);
    setActiveVisaSubTab('form');
  };

  // Update Visa Form Item
  const updateVisaFormItem = () => {
    if (editingVisaFormIndex === null) return;

    const updated = [...visaFormItems];
    updated[editingVisaFormIndex] = {
      ...updated[editingVisaFormIndex],
      ...visaFormEditData
    };

    setVisaFormItems(updated);
    resetVisaFormEdit();
  };

  // Reset Visa Form Edit
  const resetVisaFormEdit = () => {
    setEditingVisaFormIndex(null);
    setVisaFormEditData({
      type: '',
      download_action: '',
      fill_action: '',
      action1_file: null,
      action2_file: null
    });
  };

  // Handle Visa Form Edit Change
  const handleVisaFormEditChange = (e) => {
    const { name, value } = e.target;
    setVisaFormEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Visa Form File Change with Edit Support
  const handleVisaFormFileChangeWithEdit = async (index, action, file) => {
    if (!file) return;

    if (editingVisaFormIndex !== null && editingVisaFormIndex === index) {
      // If editing, update the edit data - store as File object
      setVisaFormEditData(prev => ({
        ...prev,
        [action === 'action1' ? 'action1_file' : 'action2_file']: file
      }));
    } else {
      // If not editing, update the main list
      const updated = [...visaFormItems];
      updated[index][action === 'action1' ? 'action1_file' : 'action2_file'] = file;
      setVisaFormItems(updated);
    }

    // Upload file if in edit mode
    if (isEditMode && id) {
      const visaType = editingVisaFormIndex !== null
        ? visaFormEditData.type
        : visaFormItems[index].type;

      const uploadedFileName = await handleVisaFormFileUpload(id, visaType, action, file);
      if (uploadedFileName) {
        if (editingVisaFormIndex !== null && editingVisaFormIndex === index) {
          // Update edit data with filename string
          setVisaFormEditData(prev => ({
            ...prev,
            [action === 'action1' ? 'action1_file' : 'action2_file']: uploadedFileName
          }));
        } else {
          // Update main list with filename string
          const updatedWithFilename = [...visaFormItems];
          updatedWithFilename[index][action === 'action1' ? 'action1_file' : 'action2_file'] = uploadedFileName;
          setVisaFormItems(updatedWithFilename);
        }
      }
    }
  };

  // Add Tourist Visa
  const addTouristVisa = () => {
    const trimmed = touristVisaForm.description.trim();
    if (!trimmed) return;

    const newItem = { description: trimmed };

    if (editingType === 'touristVisa' && editIndex !== -1) {
      const updated = [...touristVisaItems];
      updated[editIndex] = newItem;
      setTouristVisaItems(updated);
    } else {
      setTouristVisaItems(prev => [...prev, newItem]);
    }

    setTouristVisaForm({ description: '' });
    resetEditing();
  };

  // Add Transit Visa
  const addTransitVisa = () => {
    const trimmed = transitVisaForm.description.trim();
    if (!trimmed) return;

    const newItem = { description: trimmed };

    if (editingType === 'transitVisa' && editIndex !== -1) {
      const updated = [...transitVisaItems];
      updated[editIndex] = newItem;
      setTransitVisaItems(updated);
    } else {
      setTransitVisaItems(prev => [...prev, newItem]);
    }

    setTransitVisaForm({ description: '' });
    resetEditing();
  };

  // Add Business Visa
  const addBusinessVisa = () => {
    const trimmed = businessVisaForm.description.trim();
    if (!trimmed) return;

    const newItem = { description: trimmed };

    if (editingType === 'businessVisa' && editIndex !== -1) {
      const updated = [...businessVisaItems];
      updated[editIndex] = newItem;
      setBusinessVisaItems(updated);
    } else {
      setBusinessVisaItems(prev => [...prev, newItem]);
    }

    setBusinessVisaForm({ description: '' });
    resetEditing();
  };

  // Add Photo
  const addPhoto = () => {
    const trimmed = photoForm.description.trim();
    if (!trimmed) return;

    const newItem = { description: trimmed };

    if (editingType === 'photo' && editIndex !== -1) {
      const updated = [...photoItems];
      updated[editIndex] = newItem;
      setPhotoItems(updated);
    } else {
      setPhotoItems(prev => [...prev, newItem]);
    }

    setPhotoForm({ description: '' });
    resetEditing();
  };

  // Remove Tourist Visa
  const removeTouristVisa = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this tourist visa item?');
    if (confirmDelete) {
      setTouristVisaItems(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'touristVisa' && editIndex === idx) {
        resetEditing();
      }
    }
  };

  // Remove Transit Visa
  const removeTransitVisa = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this transit visa item?');
    if (confirmDelete) {
      setTransitVisaItems(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'transitVisa' && editIndex === idx) {
        resetEditing();
      }
    }
  };

  // Remove Business Visa
  const removeBusinessVisa = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this business visa item?');
    if (confirmDelete) {
      setBusinessVisaItems(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'businessVisa' && editIndex === idx) {
        resetEditing();
      }
    }
  };

  // Remove Photo
  const removePhoto = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this photo item?');
    if (confirmDelete) {
      setPhotoItems(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'photo' && editIndex === idx) {
        resetEditing();
      }
    }
  };

  // Function to remove a row from Submission & Pick Up
  const removeSubmissionRow = (id) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this submission row?');
    if (confirmDelete) {
      setSubmissionRows(submissionRows.filter(row => row.id !== id));
    }
  };

  // Function to remove a row
  const removeVisaFeesRow = (id) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this visa fee row?');
    if (confirmDelete) {
      setVisaFeesRows(visaFeesRows.filter(row => row.id !== id));
    }
  };

  // Visa Form Change Handlers
  const handleTouristVisaChange = (e) => {
    const { name, value } = e.target;
    setTouristVisaForm(prev => ({ ...prev, [name]: value }));
  };

  const handleTransitVisaChange = (e) => {
    const { name, value } = e.target;
    setTransitVisaForm(prev => ({ ...prev, [name]: value }));
  };

  const handleBusinessVisaChange = (e) => {
    const { name, value } = e.target;
    setBusinessVisaForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const { name, value } = e.target;
    setPhotoForm(prev => ({ ...prev, [name]: value }));
  };

  const handleVisaFeesChange = (id, field, value) => {
    const updated = visaFeesRows.map(row =>
      row.id === id ? { ...row, [field]: value } : row
    );
    setVisaFeesRows(updated);
  };

  const handleSubmissionChange = (index, field, value) => {
    const updated = [...submissionRows];
    updated[index][field] = value;
    setSubmissionRows(updated);
  };

  // Add this function to handle file uploads for visa forms
  const handleVisaFormFileChange = async (index, action, file) => {
    if (!file) return;

    const updated = [...visaFormItems];
    if (action === 'action1') {
      updated[index].action1_file = file; // Store file object
    } else {
      updated[index].action2_file = file; // Store file object
    }
    setVisaFormItems(updated);

    // If editing mode, upload immediately and get filename
    if (isEditMode && id) {
      const uploadedFileName = await handleVisaFormFileUpload(id, visaFormItems[index].type, action, file);
      if (uploadedFileName) {
        const updatedWithFilename = [...visaFormItems];
        if (action === 'action1') {
          updatedWithFilename[index].action1_file = uploadedFileName; // Store filename
        } else {
          updatedWithFilename[index].action2_file = uploadedFileName; // Store filename
        }
        setVisaFormItems(updatedWithFilename);
      }
    }
  };

  // Update this function in your frontend code
  const handleVisaFormFileUpload = async (tourId, visaType, actionType, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('visa_type', visaType);
      formData.append('action_type', actionType);

      console.log('📤 Uploading visa form file:', {
        tourId,
        visaType,
        actionType,
        fileName: file.name
      });

      const response = await fetch(`${baseurl}/api/visa/upload-file/${tourId}`, {
        method: 'POST',
        body: formData
        // Don't set Content-Type header - let browser set it with boundary
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ File uploaded successfully:', result);
        return result.fileName;
      } else {
        console.error('❌ File upload failed:', result.error);
        return null;
      }
    } catch (err) {
      console.error('❌ Upload error:', err);
      return null;
    }
  };

  // Handler for tourist visa remarks
  const handleTouristVisaRemarksChange = (e) => {
    setTouristVisaRemarks(e.target.value);
  };

  // =======================
  // TRANSPORT
  // =======================
  const [transportItem, setTransportItem] = useState({
    mode: '',
    from_city: '',
    to_city: '',
    carrier: '',
    number_code: '',
    departure_datetime: '',
    arrival_datetime: '',
    description: '',
    remarks: ''
  });
  const [transports, setTransports] = useState([]);

  const handleTransportChange = (e) => {
    const { name, value } = e.target;
    setTransportItem(prev => ({ ...prev, [name]: value }));
  };

  const addTransportRow = () => {
    if (!transportItem.description.trim()) return;

    if (editingType === 'transport' && editIndex !== -1) {
      const updated = [...transports];
      updated[editIndex] = { ...transportItem };
      setTransports(updated);
    } else {
      setTransports(prev => [...prev, { ...transportItem }]);
    }

    setTransportItem({
      mode: '',
      from_city: '',
      to_city: '',
      carrier: '',
      number_code: '',
      departure_datetime: '',
      arrival_datetime: '',
      description: '',
      remarks: ''
    });
    resetEditing();
  };

  const editTransportRow = (idx) => {
    const item = transports[idx];
    setTransportItem(item);
    setEditingItem(item);
    setEditingType('transport');
    setEditIndex(idx);
  };

  const removeTransportRow = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this transport?');
    if (confirmDelete) {
      setTransports(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'transport' && editIndex === idx) {
        resetEditing();
      }
    }
  };

  // =======================
  // BOOKING POI
  // =======================
  const [poiText, setPoiText] = useState('');
  const [poiAmount, setPoiAmount] = useState("");
  const [bookingPois, setBookingPois] = useState([]);

  const addPoi = () => {
    const txt = poiText.trim();
    if (!txt) return;

    const newPoi = { item: poiText, amount_details: poiAmount };

    if (editingType === 'poi' && editIndex !== -1) {
      // Update existing
      const updated = [...bookingPois];
      updated[editIndex] = newPoi;
      setBookingPois(updated);
      setSuccess('Booking policy updated successfully');
    } else {
      // Add new
      setBookingPois([...bookingPois, newPoi]);
      setSuccess('Booking policy added successfully');
    }

    setPoiText('');
    setPoiAmount("");
    resetEditing();
  };

  const editPoi = (idx) => {
    const poi = bookingPois[idx];
    setPoiText(poi.item);
    setPoiAmount(poi.amount_details);
    setEditingItem(poi);
    setEditingType('poi');
    setEditIndex(idx);
  };

  const removePoi = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this booking POI?');
    if (confirmDelete) {
      setBookingPois(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'poi' && editIndex === idx) {
        resetEditing();
        setPoiText('');
        setPoiAmount("");
      }
    }
  };

  // =======================
  // CANCELLATION
  // =======================
  const [cancelItem, setCancelItem] = useState({
    cancellation_policy: "",
    charges: ""
  });

  const [cancelPolicies, setCancelPolicies] = useState([]);

  const handleCancelChange = (e) => {
    const { name, value } = e.target;
    setCancelItem(prev => ({ ...prev, [name]: value }));
  };

  const addCancelRow = () => {
    if (!cancelItem.cancellation_policy.trim()) return;

    if (editingType === 'cancellation' && editIndex !== -1) {
      // Update existing
      const updated = [...cancelPolicies];
      updated[editIndex] = { ...cancelItem };
      setCancelPolicies(updated);
      setSuccess('Cancellation policy updated successfully');
    } else {
      // Add new
      setCancelPolicies(prev => [...prev, { ...cancelItem }]);
      setSuccess('Cancellation policy added successfully');
    }

    setCancelItem({ cancellation_policy: "", charges: "" });
    resetEditing();
  };

  const editCancelRow = (idx) => {
    const policy = cancelPolicies[idx];
    setCancelItem(policy);
    setEditingItem(policy);
    setEditingType('cancellation');
    setEditIndex(idx);
  };

  const removeCancelRow = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this cancellation policy?');
    if (confirmDelete) {
      setCancelPolicies(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'cancellation' && editIndex === idx) {
        resetEditing();
        setCancelItem({ cancellation_policy: "", charges: "" });
      }
    }
  };

  // Add this useEffect to set initial prefilled content for international tours
  useEffect(() => {
    // Only set default content if it's a new tour (not edit mode)
    if (!isEditMode) {
      // Prefill all remarks fields including visa remarks
      setFormData(prev => ({
        ...prev,
        cost_remarks: "The tour prices quoted are based on the number of passengers travelling together, so simply select your group size to view the applicable rate displayed alongside it. These prices are provided to give you a realistic estimate of the expected tour cost for the standard itinerary and help you plan your holiday budget with confidence. Please note that if you choose to upgrade to Deluxe or Executive category hotels, the child rates may also vary accordingly. As travel prices are influenced by factors such as hotel availability, airline fares, seasonal demand, and the transition between low and high seasons, the final tour cost may differ depending on your selected travel dates, especially when booking closer to departure. To ensure complete transparency and provide you with the most accurate and up-to-date pricing, we kindly request that you reconfirm the tour cost before making your booking and quote the tour code while enquiring. This allows us to offer you the best available rates and ensure there are no surprises later, helping you plan your journey with complete peace of mind.",
        hotel_remarks: "While we may provide hotel names or options for reference, final availability is always subject to confirmation at the time of booking. If the listed hotels are fully booked or not operational, accommodation will be arranged in hotels of a similar category, offering comparable standards and comfort; however, specific hotel selection cannot be guaranteed. Should a guest prefer accommodation at a hotel outside our regular offerings, this can be arranged at an additional cost, subject to availability.",
        transport_remarks: "For individual tour packages, travel dates are selected by the guest and remain flexible; therefore, flight fares are not included at the time of booking. Once your travel dates are finalized, we request you to contact us at 9820870771 to obtain the best available flight fares at that time. For select departures, we may have pre-blocked seats available at specially negotiated rates, subject to availability. These special fares can be viewed on our system under the online flight blocks section; however, availability is limited and may not cover all destinations. In case your preferred destination or dates are not listed, you are encouraged to contact us directly, and our team will assist you in securing the most competitive flight fares available. We do not do Train or Bus bookings so client are requested to do their own bookings to reach the start destination.",
        booking_poi_remarks: "The tour price indicated above is applicable for two passengers traveling together. Any change in the number of travellers shall be deemed a modification of the booking and may result in a revision of the tour price. Clients are advised to contact the company at the time of booking to confirm the final and accurate cost. While the company endeavours to publish rates for groups of up to ten adults, such rates are indicative and may vary on a case-by-case basis; in the event that the published rates do not meet the client’s requirements, the client is requested to communicate directly via email at salil@sktt.in. The company accepts all standard modes of payment; however, payments made via credit card may be subject to additional charges as per the applicable payment gateway and banking regulations. Upon receipt of payment, a valid invoice and receipt shall be issued to the client. All payments are subject to the company’s terms and conditions, and no adjustments, refunds, or claims shall be entertained unless explicitly agreed in writing",
        cancellation_remarks: "We understand that sometimes unforeseen circumstances may require you to cancel or modify your travel plans, and we assure you that our intention is always to assist you in the most fair and transparent manner possible. Since every booking involves multiple service providers such as airlines, hotels, transport operators, and local suppliers, cancellation charges are determined based on their respective policies and are generally calculated on a per-person basis for bookings of two passengers, while charges for larger groups may vary accordingly. To ensure accuracy and avoid any misunderstanding, we kindly request guests to confirm the applicable cancellation charges before proceeding with a cancellation request. For your security as well as ours, all cancellation requests must be submitted in writing through our official Cancellation Request Form, which serves as a clear record of your instructions and enables us to process your request promptly with the concerned suppliers. In the event of a no-show or cancellation received after the permitted cancellation period, suppliers may impose charges up to 100% of the tour cost, leaving no refundable amount. However, in genuinely unfortunate situations such as a serious medical emergency or the death of an immediate family member, we will make every possible effort to support you by requesting a refund or future travel credit from the concerned suppliers upon receipt of valid supporting documents. While any refund or credit remains entirely subject to supplier approval and cannot be guaranteed by us, we will always try our best to represent your case sympathetically. Any approved refund will be processed as soon as it is received from the suppliers and may take one week or longer, after deducting applicable service charges, administrative costs, and non-refundable components. Our commitment is to maintain complete transparency throughout the process and to ensure that every traveller is treated fairly, respectfully, and with understanding during what can often be an unexpected and difficult situation.",
        emi_remarks: "The Equated Monthly Instalment (EMI) indicated for the tour is strictly applicable to the quoted price of the specified itinerary only. Any modification to the itinerary, including but not limited to an upgrade in hotel category or an increase in the number of travellers, shall result in a revision of the total tour cost and, consequently, the EMI amount. EMIs are generally computed on a standard tenure of 12 months; however, higher loan amounts may be structured for extended tenures of up to 48 months, subject to bank policies. The final EMI amount, applicable interest rate, and loan tenure shall be determined solely based on the sanctioned loan amount and the tenure selected by the applicant, and the applicant expressly acknowledges that interest rates may vary depending on the loan value and prevailing banking norms. All EMI facilities are subject to final tour pricing and approval by the concerned bank or financial institution, which is exclusively assessed on the applicant’s CIBIL/credit score and internal credit evaluation criteria. Loan approval, rejection, interest rates, and terms are entirely at the sole discretion of the lending institution, and S K Tours & Travels shall have no role, responsibility, or influence in the sanctioning, modification, or rejection of the loan. A non-refundable processing fee of ₹500 shall be payable if a loan application is initiated, regardless of approval status, and any additional charges including but not limited to bank charges, agreement charges, stamp duty, or statutory levies shall be borne entirely by the applicant",
        optional_tour_remarks: "Optional tours are subject to availability at the destination, and for certain activities, adult and child pricing may be the same. Please note that jungle safaris are charged on a private vehicle basis rather than per person. We therefore request guests to confirm the applicable rates with us prior to booking any optional activities. Kindly be informed that once an optional tour is confirmed and tickets are purchased, the activity becomes non-refundable, even in the event of a cancellation, as these arrangements are made in advance with local service providers. We appreciate your understanding in this regard"
      }));

      // Prefill Departures Description (existing departureForm.description field)
      setDepartureForm(prev => ({
        ...prev,
        description: "All our listed tours are specially designed for independent travellers, giving you the freedom to choose your own travel dates and travel at your convenience. The itineraries shared with you are based on our extensive experience and represent the most preferred and practical travel plans; however, we completely understand that every traveller has unique preferences, and therefore customization is always possible. In fact, who better than us to help create a journey tailored perfectly to your requirements? To ensure that every traveller receives our dedicated attention and expert planning, we are pleased to offer one round of itinerary customization completely free of charge. Should you require further revisions or multiple changes thereafter, a nominal planning fee of ₹2,500 will be applicable for preparing a fresh customized itinerary and quotation. This fee is not intended as a charge but rather as a commitment towards the planning process, enabling us to devote the necessary time and resources to serious travel planning. Furthermore, if you proceed with your booking through us, the entire ₹2,500 will be adjusted against your final booking amount, meaning there is effectively no additional cost to you. Only in the event that you choose not to travel with us after the revised proposal has been prepared will the planning fee remain non-refundable. We genuinely value your trust and look forward to assisting you in creating a memorable travel experience, while ensuring that every itinerary receives the care, expertise, and attention it deserves."
      }));

      // Prefill Instruction (existing instructionText field)
      setInstructionText(
        "For your comfort, safety, and convenience during the tour, we recommend travelling light and carrying clothing suitable for the season and destination. Guests should ensure that all essential personal medications are carried along with valid prescriptions and, in case of any medical condition that may affect independent travel, obtain a fitness certificate from their doctor or consider travelling with a companion who can provide assistance if required. We also strongly recommend purchasing adequate travel insurance prior to departure, as all medical, emergency, and hospitalization expenses incurred during the tour will be the responsibility of the traveller. While we will make every reasonable effort to assist in locating appropriate medical help whenever required, please understand that our local representatives and guides can offer only limited assistance in cases involving hospitalization, and the tour itinerary may continue as scheduled. To enable us to respond quickly and responsibly in the unlikely event of an emergency, we kindly request all guests to provide the contact details of an immediate family member and, wherever applicable, their treating doctor. This information is requested purely as a precautionary measure and allows us to communicate promptly with the appropriate person should any urgent situation arise while you are travelling. Passengers travelling by air should carry all essential electronic items, including mobile phones, laptops, chargers, and power banks, in their hand baggage, subject to airline regulations, while hazardous materials are strictly prohibited. Battery-operated items should not be packed in check-in baggage unless permitted by airline guidelines. Additionally, liquids, gels, perfumes, liquid medicines, scissors, nail cutters, toothpaste, and similar items should be packed in check-in baggage only, as such items may be confiscated by airport security if carried in hand baggage. We therefore request all travellers to carefully follow airline and airport security regulations to ensure a smooth, safe, and hassle-free travel experience."
      );

      // Prefill tourist visa remarks
      setTouristVisaRemarks(
        "Visa assistance services provided by the Company are categorized under Visa on Arrival, E-Visa, and Stamp Visa. The Company acts solely as a facilitator and consultant for visa-related services and does not possess any authority to grant, approve, reject, or influence visa decisions, which remain the exclusive prerogative of the respective Embassy, Consulate, Immigration Department, or Government Authority.\n\n" +
        "1. Visa on Arrival\n" +
        "For destinations offering Visa on Arrival facilities, the Client may be required by immigration authorities to carry a minimum amount of foreign currency, generally not less than USD 500 or such amount as may be prescribed by the destination country, as proof of financial capability. The Client shall also be solely responsible for paying any applicable visa fees, taxes, or charges directly to the immigration authorities at the port of entry.\n\n" +
        "2. E-Visa Requirements\n" +
        "Where an E-Visa is issued, the Client must retain and carry the approved E-Visa in electronic format on a mobile device and/or as a printed hard copy and present the same upon demand by immigration authorities, airlines, or any competent authority during the journey.\n\n" +
        "3. Verification of Visa Details\n" +
        "Upon receipt of any visa, irrespective of its category, it shall be the sole responsibility and moral obligation of the Client to carefully verify and confirm the accuracy of all particulars appearing on the visa, including but not limited to the name, passport number, nationality, date of birth, validity period, number of entries, and all other relevant details. Any typographical, clerical, alphabetical, numerical, or factual error, however minor, may result in denial of boarding, refusal of entry, detention, deportation, or any other action by immigration authorities. The Company shall not be liable for any consequences arising from the Client's failure to verify such details before commencement of travel.\n\n" +
        "4. Submission of Documents\n" +
        "The Client expressly acknowledges and agrees that submission of documents for visa processing does not constitute or imply any assurance, representation, warranty, or guarantee of visa approval. The final decision regarding the grant, refusal, validity, duration, or conditions of any visa rests solely and exclusively with the concerned immigration authorities.\n" +
        "The Client shall be solely responsible for providing complete, accurate, authentic, valid, and genuine documents and information. Any incomplete, incorrect, misleading, forged, fabricated, fraudulent, or misrepresented documentation may result in visa refusal, cancellation, blacklisting, deportation, legal action, or any other consequences imposed by the relevant authorities, for which the Company shall bear no responsibility or liability whatsoever.\n\n" +
        "5. Additional Documentation\n" +
        "The Client acknowledges that immigration authorities, embassies, consulates, or government agencies may request additional documents, personal interviews, biometric submissions, financial records, travel history, or any other supporting information at any stage of the visa process. The Client undertakes to comply promptly with all such requirements. Any delay, refusal, or failure by the Client to provide the requested information shall be solely at the Client's risk and responsibility.\n\n" +
        "6. Non-Refundable Charges\n" +
        "All visa fees, embassy fees, government charges, service fees, consultation charges, courier charges, documentation charges, and any ancillary expenses incurred for visa processing shall be strictly non-refundable under any circumstances, irrespective of visa approval, refusal, delay, cancellation, withdrawal of application, change in travel plans, or any other reason whatsoever.\n\n" +
        "7. Limitation of Liability\n" +
        "The Client expressly acknowledges and agrees that the Company acts only as an intermediary, facilitator, and consultant for visa-related services and shall not be held responsible or liable for any visa refusal, delay, cancellation, administrative processing, change in visa conditions, denial of boarding, denial of entry, deportation, detention, financial loss, business loss, consequential damages, travel disruption, accommodation loss, or any other direct or indirect loss arising out of or connected with any decision taken by immigration authorities, embassies, consulates, airlines, or government agencies.\n\n" +
        "8. Waiver of Claims\n" +
        "By availing of the Company's services and proceeding with the visa application process, the Client voluntarily accepts and acknowledges all risks associated with visa processing and further agrees not to initiate, maintain, or pursue any legal proceedings, claims, complaints, compensation demands, disputes, or actions of any nature whatsoever against the Company, its directors, employees, representatives, associates, or agents in relation to any visa decision or action taken by the concerned authorities.\n\n" +
        "By reading this, the Client confirms that he/she has read, understood, accepted, and agreed to be bound by the above terms and conditions in their entirety. If the Client has not read the terms, he/she cannot hold our company liable for any Visa rejections or non-refundable visa fees and service charges."
      );

      // Prefill Booking Policy if empty
      if (bookingPois.length === 0) {
        setBookingPois([
          {
            item: "Per Person Booking Amount",
            amount_details: ""
          },
          {
            item: "30 Days Prior Per person cost ",
            amount_details: "50 % of the tour cost"
          },
          {
            item: "21 Days Prior Per person cost",
            amount_details: "Balance amount to pay"
          }
        ]);
      }

      // Prefill Cancellation Policy if empty
      if (cancelPolicies.length === 0) {
        setCancelPolicies([
          {
            cancellation_policy: "45 Days to 30 Days Cost per person ",
            charges: ""
          },
          {
            cancellation_policy: "30 Days to 21 Days Cost per person ",
            charges: "50% of tour cost"
          },
          {
            cancellation_policy: "21 Days till Departure date Cost per person",
            charges: "100 % Cancellation applies"
          }
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]); // Run only once when component mounts

  // =======================
  // INSTRUCTIONS
  // =======================
  const [instructionText, setInstructionText] = useState('');
  const [instructions, setInstructions] = useState([]);

  const addInstruction = () => {
    const txt = instructionText.trim();
    if (!txt) return;

    if (editingType === 'instruction' && editIndex !== -1) {
      // Update existing
      const updated = [...instructions];
      updated[editIndex] = txt;
      setInstructions(updated);
      setSuccess('Instruction updated successfully');
    } else {
      // Add new
      setInstructions(prev => [...prev, txt]);
      setSuccess('Instruction added successfully');
    }

    setInstructionText('');
    resetEditing();
  };

  const editInstruction = (idx) => {
    const instruction = instructions[idx];
    setInstructionText(instruction);
    setEditingItem(instruction);
    setEditingType('instruction');
    setEditIndex(idx);
  };

  const removeInstruction = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this instruction?');
    if (confirmDelete) {
      setInstructions(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'instruction' && editIndex === idx) {
        resetEditing();
        setInstructionText('');
      }
    }
  };

  // ITINERARIES
  const [itineraryItem, setItineraryItem] = useState({
    day: '',
    title: '',
    description: '',
    meals: {
      breakfast: false,
      lunch: false,
      dinner: false
    }
  });
  const [itineraries, setItineraries] = useState([]);

  // Edit functions for itineraries
  const editItinerary = (idx) => {
    const item = itineraries[idx];

    // Parse meals string back to checkboxes
    const mealsArray = item.meals ? item.meals.split(', ') : [];
    const meals = {
      breakfast: mealsArray.includes('Breakfast'),
      lunch: mealsArray.includes('Lunch'),
      dinner: mealsArray.includes('Dinner')
    };

    setItineraryItem({
      day: item.day,
      title: item.title,
      description: item.description || '',
      meals: meals
    });

    setEditingItem(item);
    setEditingType('itinerary');
    setEditIndex(idx);
  };

  const handleAddItinerary = () => {
    const { day, title, description, meals } = itineraryItem;
    if (!day || !title.trim()) return;

    const selectedMeals = [];
    if (meals.breakfast) selectedMeals.push('Breakfast');
    if (meals.lunch) selectedMeals.push('Lunch');
    if (meals.dinner) selectedMeals.push('Dinner');

    const mealsString = selectedMeals.join(', ');

    const newItinerary = {
      day: Number(day),
      title: title.trim(),
      description: description.trim(),
      meals: mealsString
    };

    if (editingType === 'itinerary' && editIndex !== -1) {
      // Update existing
      const updated = [...itineraries];
      updated[editIndex] = newItinerary;
      setItineraries(updated);
      setSuccess('Itinerary updated successfully');
    } else {
      // Add new
      setItineraries(prev => [...prev, newItinerary]);
      setSuccess('Itinerary added successfully');
    }

    setItineraryItem({
      day: '',
      title: '',
      description: '',
      meals: {
        breakfast: false,
        lunch: false,
        dinner: false
      }
    });
    resetEditing();
  };

  const handleRemoveItinerary = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this itinerary?');
    if (confirmDelete) {
      setItineraries(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'itinerary' && editIndex === idx) {
        resetEditing();
        setItineraryItem({
          day: '',
          title: '',
          description: '',
          meals: { breakfast: false, lunch: false, dinner: false }
        });
      }
    }
  };

  // Fetch tour data for edit mode
  useEffect(() => {
    const loadDropdownsAndTourCode = async () => {
      try {
        // Load dropdowns
        const catRes = await fetch(`${baseurl}/api/categories/all-tours`);
        const categoryData = await catRes.json();
        setCategories(Array.isArray(categoryData) ? categoryData : []);

        const destRes = await fetch(`${baseurl}/api/destinations`);
        const destData = await destRes.json();

        // Filter for international destinations only (is_domestic == 0)
        const internationalDestinations = Array.isArray(destData)
          ? destData.filter(destination => destination.is_domestic == 0)
          : [];

        // Sort destinations by name in ascending order (A to Z)
        const sortedDestinations = internationalDestinations.sort((a, b) => {
          const nameA = a.name ? a.name.toLowerCase() : '';
          const nameB = b.name ? b.name.toLowerCase() : '';

          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });

        setDestinations(sortedDestinations);

        if (isEditMode) {
          // Load existing tour data for edit
          await loadTourData();
        } else {
          // Set is_international to 1 for honeymoon tours (assuming they're international)
          setFormData(prev => ({
            ...prev,
            is_international: 1
          }));

          // Fetch tour code with both tour_type and is_international parameters
          const tourCodeRes = await fetch(
            `${baseurl}/api/tours/next-tour-code?tour_type=honeymoon&is_international=1`
          );

          if (tourCodeRes.ok) {
            const tourCodeData = await tourCodeRes.json();
            setFormData(prev => ({
              ...prev,
              tour_code: tourCodeData.next_tour_code,
              is_international: 1
            }));
          }
        }
      } catch (err) {
        setError('Failed to load dropdown data');
      }
    };

    loadDropdownsAndTourCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Load tour data for editing
  const loadTourData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch full tour data for honeymoon
      const response = await fetch(`${baseurl}/api/tours/tour/full/honeymoon/${id}`);
      if (!response.ok) throw new Error('Failed to fetch tour data');

      const data = await response.json();

      if (data.success) {
        // Set basic form data
        const basic = data.basic_details;
        setFormData({
          tour_code: basic.tour_code || '',
          tour_type: basic.tour_type || 'honeymoon',
          title: basic.title || '',
          category_id: basic.category_id || 1,
          primary_destination_id: basic.primary_destination_id || '',
          country_id: basic.country_id || '', // Add this
          duration_days: basic.duration_days || '',
          overview: basic.overview || '',
          base_price_adult: basic.base_price_adult || '',
          emi_price: basic.emi_price || '', // ← Add this line
          is_international: basic.is_international || 0,
          cost_remarks: basic.cost_remarks || '',
          hotel_remarks: basic.hotel_remarks || '',
          transport_remarks: basic.transport_remarks || '',
          booking_poi_remarks: basic.booking_poi_remarks || '',
          cancellation_remarks: basic.cancellation_remarks || '',
          emi_remarks: basic.emi_remarks || '',
          optional_tour_remarks: basic.optional_tour_remarks || '' // ← ADD THIS LINE
        });

        // Set itineraries
        if (data.itinerary && Array.isArray(data.itinerary)) {
          const formattedItineraries = data.itinerary.map(item => ({
            day: item.day,
            title: item.title,
            description: item.description || '',
            meals: item.meals || ''
          }));
          setItineraries(formattedItineraries);
        }

        // Set departures
        if (data.departures && Array.isArray(data.departures)) {
          const formattedDepartures = data.departures.map(dept => ({
            departure_date: dept.departure_date || '',
            return_date: dept.return_date || '',
            adult_price: dept.adult_price || '',
            child_price: dept.child_price || '',
            infant_price: dept.infant_price || '',
            description: dept.description || '',
            total_seats: dept.total_seats || ''
          }));
          setDepartures(formattedDepartures);
        }

        // Set inclusions
        if (data.inclusions && Array.isArray(data.inclusions)) {
          const inclusionItems = data.inclusions.map(inc => inc.item);
          setInclusions(inclusionItems);
        }

        // Set exclusions
        if (data.exclusions && Array.isArray(data.exclusions)) {
          const exclusionItems = data.exclusions.map(exc => exc.item);
          setExclusions(exclusionItems);
        }

        // Set tour costs
        if (data.costs && Array.isArray(data.costs)) {
          setTourCosts(data.costs);
        }

        // Set optional tours
        if (data.optional_tours && Array.isArray(data.optional_tours)) {
          setOptionalTours(data.optional_tours);
        }

        // Set EMI options
        if (data.emi_options && Array.isArray(data.emi_options)) {
          const defaultOptions = [
            { particulars: 'Per Month Payment', months: 6, loan_amount: '', emi: '' },
            { particulars: 'Per Month Payment', months: 12, loan_amount: '', emi: '' },
            { particulars: 'Per Month Payment', months: 18, loan_amount: '', emi: '' },
            { particulars: 'Per Month Payment', months: 24, loan_amount: '', emi: '' },
            { particulars: 'Per Month Payment', months: 30, loan_amount: '', emi: '' },
            { particulars: 'Per Month Payment', months: 36, loan_amount: '', emi: '' },
            { particulars: 'Per Month Payment', months: 48, loan_amount: '', emi: '' }
          ];

          const updatedOptions = defaultOptions.map(option => {
            const existingOption = data.emi_options.find(eo => eo.months === option.months);
            return existingOption ? {
              ...option,
              loan_amount: existingOption.loan_amount || '',
              emi: existingOption.emi || ''
            } : option;
          });

          setEmiOptions(updatedOptions);
        }

        // Set hotels
        if (data.hotels && Array.isArray(data.hotels)) {
          const formattedHotels = data.hotels.map(hotel => ({
            ...hotel,
            standard_hotel_name: hotel.standard_hotel_name || '',
            deluxe_hotel_name: hotel.deluxe_hotel_name || '',
            executive_hotel_name: hotel.executive_hotel_name || ''
          }));
          setHotelRows(formattedHotels);
        }

        // Load Visa Data
        if (data.visa_details && Array.isArray(data.visa_details)) {
          // Filter and set Tourist Visa items
          const touristVisaData = data.visa_details.filter(item => item.type === 'tourist');
          setTouristVisaItems(touristVisaData.map(item => ({ description: item.description })));

          // Filter and set Transit Visa items
          const transitVisaData = data.visa_details.filter(item => item.type === 'transit');
          setTransitVisaItems(transitVisaData.map(item => ({ description: item.description })));

          // Filter and set Business Visa items
          const businessVisaData = data.visa_details.filter(item => item.type === 'business');
          setBusinessVisaItems(businessVisaData.map(item => ({ description: item.description })));

          // Filter and set Photo items
          const photoData = data.visa_details.filter(item => item.type === 'photo');
          setPhotoItems(photoData.map(item => ({ description: item.description })));
        }

        // Load Visa Forms
        if (data.visa_forms && Array.isArray(data.visa_forms)) {
          const formattedForms = data.visa_forms.map(form => ({
            type: form.visa_type,
            download_action: form.download_action,
            fill_action: form.fill_action,
            action1_file: form.action1_file, // Keep the filename string
            action2_file: form.action2_file, // Keep the filename string
            action1_file_url: form.action1_file_url || null,
            action2_file_url: form.action2_file_url || null
          }));
          setVisaFormItems(formattedForms);

          // Load remarks from the first visa form
          if (data.visa_forms.length > 0 && data.visa_forms[0].remarks) {
            setTouristVisaRemarks(data.visa_forms[0].remarks);
          }
        }

        // Load Visa Fees
        if (data.visa_fees && Array.isArray(data.visa_fees)) {
          // Create Visa Fees rows with separate charges
          const visaFeeRows = data.visa_fees.map(fee => ({
            id: fee.fee_id || fee.id,
            type: fee.row_type,
            tourist: fee.tourist || '',
            transit: fee.transit || '',
            business: fee.business || '',
            tourist_charges: fee.tourist_charges || '',
            transit_charges: fee.transit_charges || '',
            business_charges: fee.business_charges || ''
          }));
          setVisaFeesRows(visaFeeRows);
        }

        // Load Submission Data
        if (data.visa_submission && Array.isArray(data.visa_submission)) {
          const formattedSubmissionRows = data.visa_submission.map(item => ({
            id: item.submission_id || item.id,
            label: item.label || '',
            tourist: item.tourist || '',
            transit: item.transit || '',
            business: item.business || ''
          }));
          setSubmissionRows(formattedSubmissionRows);
        }

        // Set transport
        if (data.transport && Array.isArray(data.transport)) {
          setTransports(data.transport);
        }

        // Set booking POI
        if (data.booking_poi && Array.isArray(data.booking_poi)) {
          const formattedPois = data.booking_poi.map(poi => ({
            item: poi.item,
            amount_details: poi.amount_details || ''
          }));
          setBookingPois(formattedPois);
        }

        // Set cancellation policies
        if (data.cancellation_policies && Array.isArray(data.cancellation_policies)) {
          const formattedPolicies = data.cancellation_policies.map(policy => ({
            cancellation_policy: policy.cancellation_policy,
            charges: policy.charges || ''
          }));
          setCancelPolicies(formattedPolicies);
        }

        // Set instructions
        if (data.instructions && Array.isArray(data.instructions)) {
          const instructionItems = data.instructions.map(inst => inst.item);
          setInstructions(instructionItems);
        }

        if (data.images && Array.isArray(data.images)) {
          // Keep existing images separately
          setExistingImages(data.images);
          // Set previews for new uploads (empty if no new files)
          setImagePreviews([]);
        }

        setSuccess('Tour data loaded successfully');
      }
    } catch (err) {
      setError('Failed to load tour data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // BASIC DETAILS CHANGE
  const handleBasicChange = (e) => {
    const { name, value } = e.target;

    const numericFields = [
      'duration_days',
      'base_price_adult',
      'emi_price', // ← Add this line
      'category_id',
      'primary_destination_id',
      'country_id', // Add this
      'is_international'
    ];

    const finalValue = numericFields.includes(name)
      ? value === '' ? '' : Number(value)
      : value;

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue
    }));
  };

  // DEPARTURE FORM CHANGE
  const handleDepartureChange = (e) => {
    const { name, value } = e.target;
    const numericFields = ['adult_price', 'child_price', 'total_seats', 'infant_price'];

    setDepartureForm((prev) => ({
      ...prev,
      [name]: numericFields.includes(name)
        ? value === '' ? '' : Number(value)
        : value
    }));
  };

  const handleAddDeparture = () => {
    if (!departureForm.description.trim()) return;

    if (editingType === 'departure' && editIndex !== -1) {
      const updated = [...departures];
      updated[editIndex] = { ...departureForm };
      setDepartures(updated);
      setSuccess('Departure updated successfully');
    } else {
      setDepartures(prev => [...prev, { ...departureForm }]);
      setSuccess('Departure added successfully');
    }

    setDepartureForm({
      departure_date: '',
      return_date: '',
      adult_price: '',
      child_price: '',
      infant_price: '',
      description: '',
      total_seats: ''
    });
    resetEditing();
  };

  const editDeparture = (idx) => {
    const departure = departures[idx];
    setDepartureForm(departure);
    setEditingItem(departure);
    setEditingType('departure');
    setEditIndex(idx);
  };

  const handleRemoveDeparture = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this departure?');
    if (confirmDelete) {
      setDepartures(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'departure' && editIndex === idx) {
        resetEditing();
        setDepartureForm({
          departure_date: '',
          return_date: '',
          adult_price: '',
          child_price: '',
          infant_price: '',
          description: '',
          total_seats: ''
        });
      }
    }
  };

  // EXCLUSIONS
  const handleAddExclusion = () => {
    const trimmed = exclusionText.trim();
    if (!trimmed) return;

    if (editingType === 'exclusion' && editIndex !== -1) {
      const updated = [...exclusions];
      updated[editIndex] = trimmed;
      setExclusions(updated);
      setSuccess('Exclusion updated successfully');
    } else {
      setExclusions(prev => [...prev, trimmed]);
      setSuccess('Exclusion added successfully');
    }

    setExclusionText('');
    resetEditing();
  };

  const editExclusion = (idx) => {
    const exclusion = exclusions[idx];
    setExclusionText(exclusion);
    setEditingItem(exclusion);
    setEditingType('exclusion');
    setEditIndex(idx);
  };

  const handleRemoveExclusion = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this exclusion?');
    if (confirmDelete) {
      setExclusions(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'exclusion' && editIndex === idx) {
        resetEditing();
        setExclusionText('');
      }
    }
  };

  // INCLUSIONS
  const handleAddInclusion = () => {
    const trimmed = inclusionText.trim();
    if (!trimmed) return;

    if (editingType === 'inclusion' && editIndex !== -1) {
      const updated = [...inclusions];
      updated[editIndex] = trimmed;
      setInclusions(updated);
      setSuccess('Inclusion updated successfully');
    } else {
      setInclusions(prev => [...prev, trimmed]);
      setSuccess('Inclusion added successfully');
    }

    setInclusionText('');
    resetEditing();
  };

  const editInclusion = (idx) => {
    const inclusion = inclusions[idx];
    setInclusionText(inclusion);
    setEditingItem(inclusion);
    setEditingType('inclusion');
    setEditIndex(idx);
  };

  const handleRemoveInclusion = (idx) => {
    const confirmDelete = window.confirm('Are you sure you want to remove this inclusion?');
    if (confirmDelete) {
      setInclusions(prev => prev.filter((_, i) => i !== idx));
      if (editingType === 'inclusion' && editIndex === idx) {
        resetEditing();
        setInclusionText('');
      }
    }
  };

  // IMAGES
  const handleImageChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setImageFiles(files);

    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews(prev => [...prev, ...previews]);
  };

  // Handle file selection for replacement
  const handleReplacementFileChange = (e) => {
    const file = e.target.files ? e.target.files[0] : null;
    setReplacementFile(file);
    if (file) {
      const preview = URL.createObjectURL(file);
      setReplacementPreview(preview);
    }
  };

  // Update existing image (replace with new file)
  const updateImage = async (imageId) => {
    if (!replacementFile) {
      alert('Please select a new image file to replace the existing one');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // First delete the old image
      const deleteResponse = await fetch(`${baseurl}/api/images/${imageId}`, {
        method: 'DELETE'
      });

      if (!deleteResponse.ok) {
        throw new Error('Failed to delete old image');
      }

      // Then upload the new image
      const formDataImg = new FormData();
      formDataImg.append('images', replacementFile);
      if (imageCaption.trim()) {
        formDataImg.append('caption', imageCaption.trim());
      }

      const uploadResponse = await fetch(`${baseurl}/api/images/upload/${id}`, {
        method: 'POST',
        body: formDataImg
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload new image');
      }

      // Refresh the images list
      await loadTourData();

      setSuccess('Image updated successfully');
      cancelEditImage();
    } catch (err) {
      setError('Failed to update image: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete image
  const deleteImage = async (imageId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this image?');
    if (!confirmDelete) return;

    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${baseurl}/api/images/${imageId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete image');
      }

      // Update local state
      setExistingImages(prev => prev.filter(img => img.image_id !== imageId));

      setSuccess('Image deleted successfully');
    } catch (err) {
      setError('Failed to delete image: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Set cover image
  const setCoverImage = async (imageId) => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${baseurl}/api/images/cover/${imageId}`, {
        method: 'PUT'
      });

      if (!response.ok) {
        throw new Error('Failed to set cover image');
      }

      // Update local state
      setExistingImages(prev =>
        prev.map(img => ({
          ...img,
          is_cover: img.image_id === imageId ? 1 : 0
        }))
      );

      setSuccess('Cover image updated successfully');
    } catch (err) {
      setError('Failed to set cover image: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Start editing an image
  const startEditImage = (image) => {
    setEditingImageId(image.image_id);
    setReplacementFile(null);
    setReplacementPreview(null);
  };

  // Cancel editing
  const cancelEditImage = () => {
    setEditingImageId(null);
    setReplacementFile(null);
    setReplacementPreview(null);
    // Clear the file input
    const fileInput = document.getElementById('replacementFileInput');
    if (fileInput) fileInput.value = '';
  };

  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imagePreviews]);

  // ITINERARY
  const handleItineraryChange = (e) => {
    const { name, value } = e.target;

    setItineraryItem((prev) => ({
      ...prev,
      [name]: name === 'day' ? value.replace(/[^0-9]/g, '') : value
    }));
  };

  const handleMealsCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setItineraryItem((prev) => ({
      ...prev,
      meals: {
        ...prev.meals,
        [name]: checked
      }
    }));
  };

  const goNext = () => {
    const currentIndex = TAB_LIST.indexOf(activeTab);

    // If we're on the main visa tab
    if (activeTab === 'visa') {
      // Check if there are more visa subtabs to complete
      const currentSubTabIndex = visaSubTabs.indexOf(activeVisaSubTab);

      // If current subtab is not the last one
      if (currentSubTabIndex < visaSubTabs.length - 1) {
        // Move to next visa subtab
        setActiveVisaSubTab(visaSubTabs[currentSubTabIndex + 1]);
        return;
      } else {
        // All visa subtabs are done, move to booking
        if (currentIndex < TAB_LIST.length - 1) {
          setActiveTab(TAB_LIST[currentIndex + 1]); // Move to bookingPoi
        }
        return;
      }
    }

    // Normal navigation for other tabs
    if (currentIndex < TAB_LIST.length - 1) {
      setActiveTab(TAB_LIST[currentIndex + 1]);
    }
  };

  const goBack = () => {
    const currentIndex = TAB_LIST.indexOf(activeTab);

    // If we're on the main visa tab
    if (activeTab === 'visa') {
      // Check if we can go back to previous visa subtab
      const currentSubTabIndex = visaSubTabs.indexOf(activeVisaSubTab);

      // If current subtab is not the first one
      if (currentSubTabIndex > 0) {
        // Move to previous visa subtab
        setActiveVisaSubTab(visaSubTabs[currentSubTabIndex - 1]);
        return;
      } else {
        // Go back to hotels tab from first visa subtab
        if (currentIndex > 0) {
          setActiveTab(TAB_LIST[currentIndex - 1]); // Move to hotels
        }
        return;
      }
    }

    // Normal navigation for other tabs
    if (currentIndex > 0) {
      setActiveTab(TAB_LIST[currentIndex - 1]);
    }
  };

  const handleCancel = () => {
    navigate('/intl-honeymoon-tours');
  };

  const isLastTab = activeTab === TAB_LIST[TAB_LIST.length - 1];

  // UPDATE EXISTING TOUR
  const updateTour = async () => {
    if (!formData.tour_code.trim()) {
      setError('Tour code is required');
      setActiveTab('basic');
      return;
    }
    if (!formData.title.trim()) {
      setError('Tour title is required');
      setActiveTab('basic');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // 1) PREPARE BASIC TOUR DATA FOR UPDATE
      const tourUpdateData = {
        title: formData.title.trim(),
        tour_type: formData.tour_type || 'honeymoon',
        primary_destination_id: formData.primary_destination_id,
        country_id: formData.country_id, // Add this
        duration_days: Number(formData.duration_days) || 0,
        overview: formData.overview || '',
        base_price_adult: Number(formData.base_price_adult) || 0,
        emi_price: Number(formData.emi_price) || 0, // ← Add this line
        is_international: Number(formData.is_international) || 0,
        cost_remarks: formData.cost_remarks || '',
        hotel_remarks: formData.hotel_remarks || '',
        transport_remarks: formData.transport_remarks || '',
        emi_remarks: formData.emi_remarks || '',
        booking_poi_remarks: formData.booking_poi_remarks || '',
        cancellation_remarks: formData.cancellation_remarks || '',
        optional_tour_remarks: formData.optional_tour_remarks || '' // ← ADD THIS LINE
      };

      // 1) UPDATE TOUR BASIC DETAILS
      const tourRes = await fetch(`${baseurl}/api/tours/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tourUpdateData)
      });

      const tourResponse = await tourRes.json();

      if (!tourRes.ok) {
        throw new Error(tourResponse.error || tourResponse.message || 'Failed to update tour');
      }

      // 2) DELETE EXISTING DATA
      const deleteEndpoints = [
        `${baseurl}/api/departures/bulk/${id}`,
        `${baseurl}/api/tour-costs/tour/${id}`,
        `${baseurl}/api/optional-tours/tour/${id}`,
        `${baseurl}/api/emi-options/tour/${id}`,
        `${baseurl}/api/tour-hotels/tour/${id}`,
        `${baseurl}/api/tour-transports/tour/${id}`,
        `${baseurl}/api/tour-booking-poi/tour/${id}`,
        `${baseurl}/api/tour-cancellation/tour/${id}`,
        `${baseurl}/api/tour-instructions/tour/${id}`,
        `${baseurl}/api/exclusions/tour/${id}`,
        `${baseurl}/api/inclusions/tour/${id}`,
        `${baseurl}/api/itineraries/tour/${id}`
      ];

      for (const endpoint of deleteEndpoints) {
        try {
          await fetch(endpoint, { method: 'DELETE' });
        } catch (err) {
          console.warn(`Failed to delete from ${endpoint}:`, err.message);
        }
      }

      // 3) RE-ADD ALL DATA
      // Departures
      if (departures.length > 0) {
        await fetch(`${baseurl}/api/departures/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: id, departures })
        });
      }

      // Tour Costs
      if (tourCosts.length > 0) {
        await fetch(`${baseurl}/api/tour-costs/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: id, costs: tourCosts })
        });
      }

      // Optional Tours
      if (optionalTours.length > 0) {
        await fetch(`${baseurl}/api/optional-tours/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: id, optional_tours: optionalTours })
        });
      }

      // EMI Options
      if (emiLoanAmount && !isNaN(emiLoanAmount) && emiLoanAmount > 0) {
        // Use the global loan amount
        await fetch(`${baseurl}/api/emi-options/emi/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tour_id: id,
            loan_amount: emiLoanAmount
          })
        });
      } else {
        // Fallback to existing behavior if no global loan amount
        const validEmiOptions = emiOptions.filter(opt =>
          opt.loan_amount && opt.loan_amount > 0 && opt.emi && opt.emi > 0
        );
        if (validEmiOptions.length > 0) {
          await fetch(`${baseurl}/api/emi-options/emi/bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tour_id: id,
              emi_options: validEmiOptions
            })
          });
        }
      }

      // Hotels
      if (hotelRows.length > 0) {
        await fetch(`${baseurl}/api/tour-hotels/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: id, hotels: hotelRows })
        });
      }

      // Delete existing visa data
      try {
        await fetch(`${baseurl}/api/visa/tour/${id}`, { method: 'DELETE' });
      } catch (err) {
        console.warn('Failed to delete visa data:', err.message);
      }

      // Upload visa form files FIRST
      const uploadedVisaForms = await uploadVisaFormFiles(id, visaFormItems);

      // Build visa payload (visa_type mapping + row_type/row_order fixed to match backend/loadTourData)
      const visaData = {
        tourist_visa: touristVisaItems,
        transit_visa: transitVisaItems,
        business_visa: businessVisaItems,
        visa_forms: uploadedVisaForms.map(form => ({
          visa_type: form.type, // backend field
          type: form.type, // frontend consistency
          download_action: form.download_action || 'Download',
          fill_action: form.fill_action || 'Fill Manually',
          action1_file: form.action1_file,
          action2_file: form.action2_file
        })),
        photo: [...photoItems, ...freeFlowPhotoEntries],
        visa_fees: [...visaFeesRows].map((row, index) => ({
          row_type: row.type,
          tourist: row.tourist || '',
          transit: row.transit || '',
          business: row.business || '',
          tourist_charges: row.tourist_charges || '',
          transit_charges: row.transit_charges || '',
          business_charges: row.business_charges || '',
          row_order: index
        })),
        submission: submissionRows.map((row, index) => ({
          label: row.label,
          tourist: row.tourist,
          transit: row.transit,
          business: row.business,
          row_order: index
        })),
        tourist_visa_remarks: touristVisaRemarks
      };

      if (touristVisaItems.length > 0 || transitVisaItems.length > 0 || businessVisaItems.length > 0 || photoItems.length > 0) {
        const visaResponse = await fetch(`${baseurl}/api/visa/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: id, ...visaData })
        });

        const visaResult = await visaResponse.json();

        if (!visaResponse.ok) {
          console.error('❌ Visa API Error:', visaResult);
          throw new Error(`Visa save failed: ${visaResult.error || visaResponse.statusText}`);
        }
      }

      // Transport
      if (transports.length > 0) {
        await fetch(`${baseurl}/api/tour-transports/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: id, items: transports })
        });
      }

      // Booking POI
      if (bookingPois.length > 0) {
        await fetch(`${baseurl}/api/tour-booking-poi/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: id, items: bookingPois })
        });
      }

      // Cancellation
      if (cancelPolicies.length > 0) {
        await fetch(`${baseurl}/api/tour-cancellation/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: id, policies: cancelPolicies })
        });
      }

      // Instructions
      if (instructions.length > 0) {
        await fetch(`${baseurl}/api/tour-instructions/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: id, items: instructions })
        });
      }

      // Exclusions
      if (exclusions.length > 0) {
        await fetch(`${baseurl}/api/exclusions/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: id, items: exclusions })
        });
      }

      // Inclusions
      if (inclusions.length > 0) {
        await fetch(`${baseurl}/api/inclusions/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: id, items: inclusions })
        });
      }

      // Itineraries
      if (itineraries.length > 0) {
        const itineraryPayload = itineraries.map((item) => ({
          ...item,
          tour_id: id
        }));

        await fetch(`${baseurl}/api/itineraries/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(itineraryPayload)
        });
      }

      // Images (only if new files added)
      if (imageFiles.length > 0) {
        const formDataImages = new FormData();
        imageFiles.forEach((file) => {
          formDataImages.append('images', file);
        });

        if (imageCaption.trim()) {
          formDataImages.append('caption', imageCaption.trim());
        }

        await fetch(`${baseurl}/api/images/upload/${id}`, {
          method: 'POST',
          body: formDataImages
        });
      }

      setSuccess('Tour updated successfully!');
      setTimeout(() => navigate('/intl-honeymoon-tours'), 1500);
    } catch (err) {
      console.error('Error updating tour:', err);
      setError(err.message || 'Failed to update tour');
    } finally {
      setLoading(false);
    }
  };

  // CREATE NEW TOUR
  const createTour = async () => {
    if (!formData.tour_code.trim()) {
      setError('Tour code is required');
      setActiveTab('basic');
      return;
    }
    if (!formData.title.trim()) {
      setError('Tour title is required');
      setActiveTab('basic');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // 1) CREATE TOUR
      const tourRes = await fetch(`${baseurl}/api/tours`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          country_id: formData.country_id, // Add this
          optional_tour_remarks: formData.optional_tour_remarks || '',
          transport_remarks: formData.transport_remarks || ''
        })
      });

      if (!tourRes.ok) {
        const err = await tourRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create tour');
      }

      const tourData = await tourRes.json();
      const tourId = tourData.tour_id || tourData.id || tourData.insertId;

      // Upload visa form files FIRST
      const uploadedVisaForms = await uploadVisaFormFiles(tourId, visaFormItems);

      // 2) DEPARTURES BULK
      if (departures.length > 0) {
        await fetch(`${baseurl}/api/departures/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: tourId, departures })
        });
      }

      // 3) TOUR COSTS BULK
      if (tourCosts.length > 0) {
        await fetch(`${baseurl}/api/tour-costs/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: tourId, costs: tourCosts })
        });
      }

      // 4) OPTIONAL TOURS BULK
      if (optionalTours.length > 0) {
        await fetch(`${baseurl}/api/optional-tours/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: tourId, optional_tours: optionalTours })
        });
      }

      // 5) EMI OPTIONS BULK
      if (emiLoanAmount && !isNaN(emiLoanAmount) && emiLoanAmount > 0) {
        // Use the global loan amount
        await fetch(`${baseurl}/api/emi-options/emi/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tour_id: tourId,
            loan_amount: emiLoanAmount
          })
        });
      } else {
        // Fallback to existing behavior if no global loan amount
        const validEmiOptions = emiOptions.filter(opt =>
          opt.loan_amount && opt.loan_amount > 0 && opt.emi && opt.emi > 0
        );
        if (validEmiOptions.length > 0) {
          await fetch(`${baseurl}/api/emi-options/emi/bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tour_id: tourId,
              emi_options: validEmiOptions
            })
          });
        }
      }

      // 6) HOTELS BULK
      if (hotelRows.length > 0) {
        await fetch(`${baseurl}/api/tour-hotels/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: tourId, hotels: hotelRows })
        });
      }

      // Save Visa Data (only for international tours)
      const visaData = {
        tourist_visa: touristVisaItems,
        transit_visa: transitVisaItems,
        business_visa: businessVisaItems,
        visa_forms: uploadedVisaForms.map(form => ({
          visa_type: form.type, // backend field
          type: form.type, // frontend consistency
          download_action: form.download_action || 'Download',
          fill_action: form.fill_action || 'Fill Manually',
          action1_file: form.action1_file, // Filename string
          action2_file: form.action2_file  // Filename string
        })),
        photo: [...photoItems, ...freeFlowPhotoEntries],
        visa_fees: [...visaFeesRows].map((row, index) => ({
          row_type: row.type,
          tourist: row.tourist || '',
          transit: row.transit || '',
          business: row.business || '',
          tourist_charges: row.tourist_charges || '',
          transit_charges: row.transit_charges || '',
          business_charges: row.business_charges || '',
          row_order: index
        })),
        submission: submissionRows.map((row, index) => ({
          label: row.label,
          tourist: row.tourist,
          transit: row.transit,
          business: row.business,
          row_order: index
        })),
        tourist_visa_remarks: touristVisaRemarks
      };

      if (touristVisaItems.length > 0 || transitVisaItems.length > 0 || businessVisaItems.length > 0 || photoItems.length > 0) {
        const visaResponse = await fetch(`${baseurl}/api/visa/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: tourId, ...visaData })
        });

        const visaResult = await visaResponse.json();

        if (!visaResponse.ok) {
          console.error('❌ Visa API Error:', visaResult);
          throw new Error(`Visa save failed: ${visaResult.error || visaResponse.statusText}`);
        }
      }

      // 7) TRANSPORT BULK
      if (transports.length > 0) {
        await fetch(`${baseurl}/api/tour-transports/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: tourId, items: transports })
        });
      }

      // 8) BOOKING POI BULK
      if (bookingPois.length > 0) {
        await fetch(`${baseurl}/api/tour-booking-poi/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: tourId, items: bookingPois })
        });
      }

      // 9) CANCELLATION BULK
      if (cancelPolicies.length > 0) {
        await fetch(`${baseurl}/api/tour-cancellation/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: tourId, policies: cancelPolicies })
        });
      }

      // 10) INSTRUCTIONS BULK
      if (instructions.length > 0) {
        await fetch(`${baseurl}/api/tour-instructions/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: tourId, items: instructions })
        });
      }

      // 11) EXCLUSIONS
      if (exclusions.length > 0) {
        await fetch(`${baseurl}/api/exclusions/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: tourId, items: exclusions })
        });
      }

      // 12) IMAGES
      if (imageFiles.length > 0) {
        const formDataImages = new FormData();
        imageFiles.forEach((file) => {
          formDataImages.append('images', file);
        });

        if (imageCaption.trim()) {
          formDataImages.append('caption', imageCaption.trim());
        }

        await fetch(`${baseurl}/api/images/upload/${tourId}`, {
          method: 'POST',
          body: formDataImages
        });
      }

      // 13) INCLUSIONS
      if (inclusions.length > 0) {
        await fetch(`${baseurl}/api/inclusions/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tour_id: tourId, items: inclusions })
        });
      }

      // 14) ITINERARY DAYS
      if (itineraries.length > 0) {
        const payload = itineraries.map((item) => ({
          ...item,
          tour_id: tourId
        }));

        await fetch(`${baseurl}/api/itineraries/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      setSuccess('Tour saved successfully!');
      setTimeout(() => navigate('/intl-honeymoon-tours'), 1500);
    } catch (err) {
      setError(err.message || 'Failed to save tour');
    } finally {
      setLoading(false);
    }
  };

  const uploadVisaFormFiles = async (tourId, visaForms) => {
    const uploadedForms = [];

    for (const form of visaForms) {
      const formData = {
        type: form.type,
        download_action: form.download_action,
        fill_action: form.fill_action,
        action1_file: null,
        action2_file: null
      };

      // Upload action1_file (PDF) if exists
      if (form.action1_file) {
        if (typeof form.action1_file === 'object' && form.action1_file instanceof File) {
          const fileName = await handleVisaFormFileUpload(tourId, form.type, 'action1', form.action1_file);
          if (fileName) {
            formData.action1_file = fileName;
          }
        } else if (typeof form.action1_file === 'string') {
          // Already a filename (from database or previous upload)
          formData.action1_file = form.action1_file;
        }
      }

      // Upload action2_file (Word) if exists
      if (form.action2_file) {
        if (typeof form.action2_file === 'object' && form.action2_file instanceof File) {
          const fileName = await handleVisaFormFileUpload(tourId, form.type, 'action2', form.action2_file);
          if (fileName) {
            formData.action2_file = fileName;
          }
        } else if (typeof form.action2_file === 'string') {
          // Already a filename (from database or previous upload)
          formData.action2_file = form.action2_file;
        }
      }

      uploadedForms.push(formData);
    }

    return uploadedForms;
  };

  const handleSaveClick = () => {
    if (isLastTab) {
      if (isEditMode) {
        updateTour();
      } else {
        createTour();
      }
    } else {
      // Check if current visa subtab is complete before moving on
      if (activeTab === 'visa') {
        // Remove mandatory validation - visa sections can be empty
        // Simply allow navigation to next tab/subtab
        const currentSubTabIndex = visaSubTabs.indexOf(activeVisaSubTab);

        // If current subtab is not the last one
        if (currentSubTabIndex < visaSubTabs.length - 1) {
          // Move to next visa subtab
          setActiveVisaSubTab(visaSubTabs[currentSubTabIndex + 1]);
        } else {
          // All visa subtabs are done, move to booking
          const currentIndex = TAB_LIST.indexOf(activeTab);
          if (currentIndex < TAB_LIST.length - 1) {
            setActiveTab(TAB_LIST[currentIndex + 1]); // Move to bookingPoi
          }
        }
        return;
      }

      goNext();
    }
  };

  const handleDepartureDateSelect = (e) => {
    const departureDate = e.target.value;
    const duration = formData.duration_days;

    if (!departureDate || !duration) {
      setDepartureForm(prev => ({ ...prev, departure_date: departureDate }));
      return;
    }

    const dateObj = new Date(departureDate);
    dateObj.setDate(dateObj.getDate() + Number(duration));

    const returnISO = dateObj.toISOString().split("T")[0];

    setDepartureForm(prev => ({
      ...prev,
      departure_date: departureDate,
      return_date: returnISO
    }));
  };

  // Dynamic "+ Add ..." button for bottom bar
  const getAddConfigForTab = (tabKey) => {
    switch (tabKey) {
      case 'itineraries':
        return {
          label: editingType === 'itinerary' && editIndex !== -1 ? '✓ Update Itinerary' : '+ Add Day',
          onClick: handleAddItinerary
        };
      case 'departures':
        return {
          label: editingType === 'departure' && editIndex !== -1 ? '✓ Update Departure' : '+ Add Departure',
          onClick: handleAddDeparture
        };
      case 'costs':
        return {
          label: editingType === 'cost' && editIndex !== -1 ? '✓ Update Cost Row' : '+ Add Cost Row',
          onClick: addCostRow
        };
      case 'optionalTours':
        return {
          label: editingType === 'optionalTour' && editIndex !== -1 ? '✓ Update Optional Tour' : '+ Add Optional Tour',
          onClick: addOptionalTourRow
        };
      case 'inclusions':
        return {
          label: editingType === 'inclusion' && editIndex !== -1 ? '✓ Update Inclusion' : '+ Add Inclusion',
          onClick: handleAddInclusion
        };
      case 'exclusions':
        return {
          label: editingType === 'exclusion' && editIndex !== -1 ? '✓ Update Exclusion' : '+ Add Exclusion',
          onClick: handleAddExclusion
        };
      case 'transport':
        return {
          label: editingType === 'transport' && editIndex !== -1 ? '✓ Update Flights' : '+ Add Flights',
          onClick: addTransportRow
        };
      case 'hotels':
        return {
          label: editingType === 'hotel' && editIndex !== -1 ? '✓ Update Hotel' : '+ Add Hotel',
          onClick: addHotelRow
        };
      case 'visa':
        // Check which subtab is active
        if (activeVisaSubTab === 'tourist') {
          return {
            label: editingType === 'touristVisa' ? '✓ Update Tourist Visa' : '+ Add Tourist Visa',
            onClick: addTouristVisa
          };
        } else if (activeVisaSubTab === 'transit') {
          return {
            label: editingType === 'transitVisa' ? '✓ Update Transit Visa' : '+ Add Transit Visa',
            onClick: addTransitVisa
          };
        } else if (activeVisaSubTab === 'business') {
          return {
            label: editingType === 'businessVisa' ? '✓ Update Business Visa' : '+ Add Business Visa',
            onClick: addBusinessVisa
          };
        } else if (activeVisaSubTab === 'photo') {
          return {
            label: editingType === 'photo' ? '✓ Update Photo' : '+ Add Photo',
            onClick: addPhoto
          };
        } else if (activeVisaSubTab === 'form') {
          if (editingVisaFormIndex !== null) {
            return {
              label: '✓ Update Visa Form',
              onClick: updateVisaFormItem
            };
          }
        }
        return null;
      case 'bookingPoi':
        return {
          label: editingType === 'poi' && editIndex !== -1 ? '✓ Update Booking Policy' : '+ Add Booking Policy',
          onClick: addPoi
        };
      case 'cancellation':
        return {
          label: editingType === 'cancellation' && editIndex !== -1 ? '✓ Update Cancellation Policy' : '+ Add Cancellation Policy',
          onClick: addCancelRow
        };
      case 'instructions':
        return {
          label: editingType === 'instruction' && editIndex !== -1 ? '✓ Update Instruction' : '+ Add Instruction',
          onClick: addInstruction
        };
      default:
        return null;
    }
  };

  const addConfig = getAddConfigForTab(activeTab);

  return (
    <Navbar>
      <Container>
        <h2 className="mb-4">{isEditMode ? 'Edit Honeymoon Tour' : 'Add Honeymoon Tour'}</h2>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* ======== BUTTONS ======== */}
        <div className="d-flex justify-content-end gap-2 mt-4 mb-4">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            variant="secondary"
            onClick={goBack}
            disabled={activeTab === 'basic' || loading}
          >
            Back
          </Button>

          {addConfig && (
            <Button
              variant="success"
              onClick={addConfig.onClick}
              disabled={loading}
            >
              {addConfig.label}
            </Button>
          )}

          <Button
            variant="primary"
            onClick={handleSaveClick}
            disabled={loading}
          >
            {loading ? 'Saving...' :
              isLastTab ? (isEditMode ? 'Update All' : 'Save All') :
                activeTab === 'visa' ? `Save & Next (${visaSubTabs.indexOf(activeVisaSubTab) + 1}/${visaSubTabs.length})` :
                  'Save & Continue'}
          </Button>
        </div>

        <Card>
          <Card.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-4"
            >
              {/* ======== TAB 1: BASIC DETAILS ======== */}
              <Tab eventKey="basic" title="Basic Details">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tour Code *</Form.Label>
                      <Form.Control
                        type="text"
                        name="tour_code"
                        value={formData.tour_code}
                        onChange={handleBasicChange}
                        readOnly
                        disabled={isEditMode}
                        style={{
                          cursor: isEditMode ? "not-allowed" : "default",
                          fontWeight: "bold",
                          backgroundColor: isEditMode ? "#f8f9fa" : "white"
                        }}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Tour Title *</Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleBasicChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Tour Price *</Form.Label>
                      <Form.Control
                        type="number"
                        name="base_price_adult"
                        value={formData.base_price_adult}
                        onChange={handleBasicChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>International Destinations *</Form.Label>
                      <Form.Select
                        name="primary_destination_id"
                        value={formData.primary_destination_id}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedDestination = destinations.find(d => d.destination_id == selectedId);

                          // Update both destination_id and country_id
                          setFormData(prev => ({
                            ...prev,
                            primary_destination_id: selectedId,
                            country_id: selectedDestination ? selectedDestination.country_id : ''
                          }));
                        }}
                      >
                        <option value="">Select Destination</option>
                        {destinations.map((d) => (
                          <option
                            key={d.destination_id}
                            value={d.destination_id}
                          >
                            {d.name} ({d.country_name})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Duration Days *</Form.Label>
                      <Form.Control
                        type="number"
                        name="duration_days"
                        value={formData.duration_days}
                        onChange={handleBasicChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>EMI Price</Form.Label>
                      <Form.Control
                        type="number"
                        name="emi_price"
                        value={formData.emi_price}
                        onChange={handleBasicChange}
                        placeholder="Optional EMI price"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="itineraries" title="Itineraries">
                <Row>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Day</Form.Label>
                      <Form.Control
                        type="number"
                        name="day"
                        value={itineraryItem.day}
                        onChange={handleItineraryChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        value={itineraryItem.title}
                        onChange={handleItineraryChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Meals</Form.Label>
                      <div className="d-flex gap-3">
                        <Form.Check
                          type="checkbox"
                          label="Breakfast"
                          name="breakfast"
                          checked={itineraryItem.meals.breakfast}
                          onChange={handleMealsCheckboxChange}
                        />
                        <Form.Check
                          type="checkbox"
                          label="Lunch"
                          name="lunch"
                          checked={itineraryItem.meals.lunch}
                          onChange={handleMealsCheckboxChange}
                        />
                        <Form.Check
                          type="checkbox"
                          label="Dinner"
                          name="dinner"
                          checked={itineraryItem.meals.dinner}
                          onChange={handleMealsCheckboxChange}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={itineraryItem.description}
                    onChange={handleItineraryChange}
                  />
                </Form.Group>

                {itineraries.length > 0 && (
                  <Table striped bordered hover size="sm" className="mt-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Day</th>
                        <th>Title</th>
                        <th>Meals</th>
                        <th>Description</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itineraries
                        .sort((a, b) => a.day - b.day)
                        .map((item, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{item.day}</td>
                            <td>{item.title}</td>
                            <td>{item.meals || '-'}</td>
                            <td>{item.description || '-'}</td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => editItinerary(idx)}
                                  title="Edit"
                                >
                                  <Pencil size={14} />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleRemoveItinerary(idx)}
                                  title="Remove"
                                >
                                  <Trash size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              <Tab eventKey="departures" title="Departures">
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Departures Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="description"
                        value={departureForm.description}
                        onChange={handleDepartureChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {departures.length > 0 && (
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Description</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departures.map((dep, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{dep.description || '-'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => editDeparture(idx)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveDeparture(idx)}
                                title="Remove"
                              >
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              <Tab eventKey="costs" title="Tour Cost">
                <Row className="align-items-end">
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Pax *</Form.Label>
                      <Form.Control
                        type="number"
                        name="pax"
                        value={tourCostItem.pax}
                        onChange={handleCostChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Standard Hotel</Form.Label>
                      <Form.Control
                        type="number"
                        name="standard_hotel"
                        value={tourCostItem.standard_hotel}
                        onChange={handleCostChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Deluxe Hotel</Form.Label>
                      <Form.Control
                        type="number"
                        name="deluxe_hotel"
                        value={tourCostItem.deluxe_hotel}
                        onChange={handleCostChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Executive Hotel</Form.Label>
                      <Form.Control
                        type="number"
                        name="executive_hotel"
                        value={tourCostItem.executive_hotel}
                        onChange={handleCostChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Child With Bed</Form.Label>
                      <Form.Control
                        type="number"
                        name="child_with_bed"
                        value={tourCostItem.child_with_bed}
                        onChange={handleCostChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Child No Bed</Form.Label>
                      <Form.Control
                        type="number"
                        name="child_no_bed"
                        value={tourCostItem.child_no_bed}
                        onChange={handleCostChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mt-3">
                  <Form.Label>Cost Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="cost_remarks"
                    value={formData.cost_remarks}
                    onChange={handleBasicChange}
                  />
                </Form.Group>

                {tourCosts.length > 0 && (
                  <Table striped bordered hover size="sm" className="mt-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Pax</th>
                        <th>Standard</th>
                        <th>Deluxe</th>
                        <th>Executive</th>
                        <th>Chd Bed</th>
                        <th>Chd NoBed</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tourCosts.map((c, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{c.pax}</td>
                          <td>{c.standard_hotel || 'NA'}</td>
                          <td>{c.deluxe_hotel || 'NA'}</td>
                          <td>{c.executive_hotel || 'NA'}</td>
                          <td>{c.child_with_bed || 'NA'}</td>
                          <td>{c.child_no_bed || 'NA'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => editCostRow(idx)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeCostRow(idx)}
                                title="Remove"
                              >
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              <Tab eventKey="optionalTours" title="Optional Tour">
                <Row className="align-items-end">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Tour Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="tour_name"
                        value={optionalTourItem.tour_name}
                        onChange={handleOptionalTourChange}
                        placeholder="Enter optional tour name"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Adult Price</Form.Label>
                      <Form.Control
                        type="text"
                        name="adult_price"
                        value={optionalTourItem.adult_price}
                        onChange={handleOptionalTourChange}
                        placeholder="Enter adult price"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Child Price</Form.Label>
                      <Form.Control
                        type="text"
                        name="child_price"
                        value={optionalTourItem.child_price}
                        onChange={handleOptionalTourChange}
                        placeholder="Enter child price"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mt-3">
                  <Form.Label>Optional Tour Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="optional_tour_remarks"
                    value={formData.optional_tour_remarks || ''}
                    onChange={handleBasicChange}
                    placeholder="Enter any remarks for optional tours..."
                  />
                </Form.Group>

                {optionalTours.length > 0 && (
                  <Table striped bordered hover size="sm" className="mt-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Tour Name</th>
                        <th>Adult Price</th>
                        <th>Child Price</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {optionalTours.map((tour, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{tour.tour_name}</td>
                          <td>{tour.adult_price || 'NA'}</td>
                          <td>{tour.child_price || 'NA'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => editOptionalTourRow(idx)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeOptionalTourRow(idx)}
                                title="Remove"
                              >
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              <Tab eventKey="emiOptions" title="EMI Options">
                <Card className="mb-4">
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Loan Amount *</Form.Label>
                          <InputGroup>
                            <InputGroup.Text>₹</InputGroup.Text>
                            <Form.Control
                              type="number"
                              min="0"
                              step="1000"
                              value={emiLoanAmount}
                              onChange={(e) => handleLoanAmountChange(e.target.value)}
                              placeholder="Enter loan amount"
                            />
                          </InputGroup>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Interest Rate (%) *</Form.Label>
                          <InputGroup>
                            <InputGroup.Text>%</InputGroup.Text>
                            <Form.Control
                              type="number"
                              min="0"
                              max="50"
                              step="0.1"
                              value={emiInterestRate}
                              onChange={(e) => setEmiInterestRate(e.target.value)}
                            />
                          </InputGroup>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Table striped bordered hover responsive className="align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th width="5%">#</th>
                      <th width="30%">Particulars</th>
                      <th width="25%">Loan Amount</th>
                      <th width="15%">Months</th>
                      <th width="25%">EMI (Calculated)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emiOptions.map((option, index) => (
                      <tr key={index}>
                        <td className="text-center">{index + 1}</td>
                        <td>
                          <Form.Control
                            type="text"
                            value={option.particulars}
                            readOnly
                            plaintext
                            className="border-0 bg-transparent"
                          />
                        </td>
                        <td>
                          <Form.Group className="mb-0">
                            <InputGroup>
                              <InputGroup.Text>₹</InputGroup.Text>
                              <Form.Control
                                type="number"
                                min="0"
                                step="1000"
                                value={option.loan_amount || ''}
                                onChange={(e) => {
                                  const updatedOptions = [...emiOptions];
                                  updatedOptions[index].loan_amount = e.target.value;
                                  updatedOptions[index].emi = calculateEMI(
                                    e.target.value,
                                    option.months,
                                    emiInterestRate
                                  );
                                  setEmiOptions(updatedOptions);
                                }}
                                placeholder="Enter amount"
                                readOnly={!!emiLoanAmount} // Make read-only if global loan amount is set
                              />
                            </InputGroup>
                          </Form.Group>
                        </td>
                        <td className="text-center">
                          <Form.Control
                            type="text"
                            value={option.months}
                            readOnly
                            plaintext
                            className="border-0 bg-transparent text-center"
                          />
                        </td>
                        <td>
                          <Form.Group className="mb-0">
                            <InputGroup>
                              <InputGroup.Text>₹</InputGroup.Text>
                              <Form.Control
                                type="number"
                                min="0"
                                step="100"
                                value={option.emi || ''}
                                readOnly
                                className="bg-light"
                                style={{ fontWeight: 'bold' }}
                              />
                            </InputGroup>
                          </Form.Group>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Form.Group className="mt-4">
                  <Form.Label>EMI Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="emi_remarks"
                    value={formData.emi_remarks}
                    onChange={handleBasicChange}
                    placeholder="Enter any EMI-related remarks or notes..."
                  />
                </Form.Group>
              </Tab>

              <Tab eventKey="inclusions" title="Inclusions">
                <Form.Group className="mb-3">
                  <Form.Label>Inclusion</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={inclusionText}
                    onChange={(e) => setInclusionText(e.target.value)}
                    placeholder="Type an inclusion"
                  />
                </Form.Group>

                {inclusions.length > 0 && (
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Inclusion</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inclusions.map((item, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{item}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => editInclusion(idx)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveInclusion(idx)}
                                title="Remove"
                              >
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              <Tab eventKey="exclusions" title="Exclusions">
                <Form.Group className="mb-3">
                  <Form.Label>Exclusion</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={exclusionText}
                    onChange={(e) => setExclusionText(e.target.value)}
                    placeholder="Type an exclusion"
                  />
                </Form.Group>

                {exclusions.length > 0 && (
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Exclusion</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exclusions.map((item, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{item}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => editExclusion(idx)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveExclusion(idx)}
                                title="Remove"
                              >
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              <Tab eventKey="transport" title="Flights">
                <Form.Group className="mt-3">
                  <Form.Label>Flights Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="transport_remarks"
                    value={formData.transport_remarks}
                    onChange={handleBasicChange}
                  />
                </Form.Group>

                {transports.length > 0 && (
                  <Table striped bordered hover size="sm" className="mt-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Description</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transports.map((t, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{t.description || 'NA'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => editTransportRow(idx)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeTransportRow(idx)}
                                title="Remove"
                              >
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              <Tab eventKey="hotels" title="Hotels">
                <Row className="align-items-end">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>City *</Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={hotelItem.city}
                        onChange={handleHotelChange}
                        placeholder="Enter city name"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Nights</Form.Label>
                      <Form.Control
                        type="number"
                        name="nights"
                        value={hotelItem.nights}
                        onChange={handleHotelChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mt-3">
                      <Form.Label>Standard Hotel Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="standard_hotel_name"
                        value={hotelItem.standard_hotel_name}
                        onChange={handleHotelChange}
                        placeholder="Enter standard hotel name"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mt-3">
                      <Form.Label>Deluxe Hotel Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="deluxe_hotel_name"
                        value={hotelItem.deluxe_hotel_name}
                        onChange={handleHotelChange}
                        placeholder="Enter deluxe hotel name"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mt-3">
                      <Form.Label>Executive Hotel Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="executive_hotel_name"
                        value={hotelItem.executive_hotel_name}
                        onChange={handleHotelChange}
                        placeholder="Enter executive hotel name"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mt-3">
                  <Form.Label>Hotel Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="hotel_remarks"
                    value={formData.hotel_remarks}
                    onChange={handleBasicChange}
                  />
                </Form.Group>

                {hotelRows.length > 0 && (
                  <Table striped bordered hover size="sm" className="mt-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>City</th>
                        <th>Nights</th>
                        <th>Standard Hotel</th>
                        <th>Deluxe Hotel</th>
                        <th>Executive Hotel</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hotelRows.map((h, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{h.city}</td>
                          <td>{h.nights}</td>
                          <td>{h.standard_hotel_name || '-'}</td>
                          <td>{h.deluxe_hotel_name || '-'}</td>
                          <td>{h.executive_hotel_name || '-'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => editHotelRow(idx)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeHotelRow(idx)}
                                title="Remove"
                              >
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              {/* ======== VISA TAB ======== */}
              <Tab eventKey="visa" title="Visa">
                <Tabs
                  activeKey={activeVisaSubTab}
                  onSelect={(k) => setActiveVisaSubTab(k)}
                  className="mb-4"
                >
                  {/* Subtab 1: Tourist Visa */}
                  <Tab eventKey="tourist" title="Tourist Visa">
                    <Form.Group className="mb-3">
                      <Form.Label>Tourist Visa</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="description"
                        value={touristVisaForm.description}
                        onChange={handleTouristVisaChange}
                        placeholder="Enter tourist visa details"
                      />
                    </Form.Group>

                    {touristVisaItems.length > 0 && (
                      <Table striped bordered hover size="sm" className="mt-3">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Description</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {touristVisaItems.map((item, idx) => (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td>{item.description || '-'}</td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => editTouristVisa(idx)}
                                    title="Edit"
                                  >
                                    <Pencil size={14} />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeTouristVisa(idx)}
                                    title="Remove"
                                  >
                                    <Trash size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Tab>

                  {/* Subtab 2: Transit Visa */}
                  <Tab eventKey="transit" title="Transit Visa">
                    <Form.Group className="mb-3">
                      <Form.Label>Transit Visa</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="description"
                        value={transitVisaForm.description}
                        onChange={handleTransitVisaChange}
                        placeholder="Enter transit visa details"
                      />
                    </Form.Group>

                    {transitVisaItems.length > 0 && (
                      <Table striped bordered hover size="sm" className="mt-3">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Description</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transitVisaItems.map((item, idx) => (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td>{item.description || '-'}</td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => editTransitVisa(idx)}
                                    title="Edit"
                                  >
                                    <Pencil size={14} />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeTransitVisa(idx)}
                                    title="Remove"
                                  >
                                    <Trash size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Tab>

                  {/* Subtab 3: Business Visa */}
                  <Tab eventKey="business" title="Business Visa">
                    <Form.Group className="mb-3">
                      <Form.Label>Business Visa</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="description"
                        value={businessVisaForm.description}
                        onChange={handleBusinessVisaChange}
                        placeholder="Enter business visa details"
                      />
                    </Form.Group>

                    {businessVisaItems.length > 0 && (
                      <Table striped bordered hover size="sm" className="mt-3">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Description</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {businessVisaItems.map((item, idx) => (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td>{item.description || '-'}</td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => editBusinessVisa(idx)}
                                    title="Edit"
                                  >
                                    <Pencil size={14} />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeBusinessVisa(idx)}
                                    title="Remove"
                                  >
                                    <Trash size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Tab>

                  {/* Subtab 4: Visa Form */}
                  <Tab eventKey="form" title="Visa Form">
                    <div className="mb-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          const newVisaForm = {
                            type: 'New Visa Type',
                            download_action: 'Download',
                            fill_action: 'Fill Manually',
                            action1_file: null,
                            action2_file: null
                          };
                          setVisaFormItems([...visaFormItems, newVisaForm]);
                        }}
                      >
                        + Add New Visa Form
                      </Button>
                    </div>

                    {visaFormItems.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-muted">No visa forms added yet. Click "Add New Visa Form" to get started.</p>
                      </div>
                    ) : (
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th width="20%">Visa Type</th>
                            <th width="35%">Upload PDF</th>
                            <th width="35%">Upload Word</th>
                            <th width="10%">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visaFormItems.map((item, idx) => {
                            const pdfFile = item.action1_file;
                            const wordFile = item.action2_file;
                            const pdfUrl = getFileUrl(pdfFile);
                            const wordUrl = getFileUrl(wordFile);

                            return (
                              <tr key={idx}>
                                <td>
                                  <Form.Control
                                    type="text"
                                    value={item.type}
                                    onChange={(e) => {
                                      const updated = [...visaFormItems];
                                      updated[idx].type = e.target.value;
                                      setVisaFormItems(updated);
                                    }}
                                    placeholder="Enter visa type"
                                    size="sm"
                                  />
                                </td>
                                <td>
                                  <div className="d-flex flex-column gap-2">
                                    <div>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => document.getElementById(`pdf-upload-${idx}`).click()}
                                      >
                                        {pdfFile ? 'Change PDF' : 'Upload PDF'}
                                      </Button>
                                      <Form.Control
                                        type="file"
                                        id={`pdf-upload-${idx}`}
                                        accept=".pdf"
                                        className="d-none"
                                        onChange={(e) => handleVisaFormFileChange(idx, 'action1', e.target.files[0])}
                                      />
                                    </div>

                                    {pdfFile && (
                                      <div className="d-flex align-items-center justify-content-between bg-light p-2 rounded">
                                        <div className="flex-grow-1">
                                          <small className="text-success d-block">
                                            <strong>✓ Uploaded:</strong>
                                          </small>
                                          <small className="text-muted d-block">
                                            {typeof pdfFile === 'string' ? pdfFile : pdfFile.name}
                                          </small>
                                        </div>

                                        {pdfUrl && (
                                          <div className="ms-2">
                                            <Button
                                              variant="outline-info"
                                              size="sm"
                                              onClick={() => openFileInNewTab(pdfUrl)}
                                              title="View PDF"
                                              className="d-flex align-items-center"
                                            >
                                              👁️ View
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>

                                <td>
                                  <div className="d-flex flex-column gap-2">
                                    <div>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => document.getElementById(`word-upload-${idx}`).click()}
                                      >
                                        {wordFile ? 'Change Word' : 'Upload Word'}
                                      </Button>
                                      <Form.Control
                                        type="file"
                                        id={`word-upload-${idx}`}
                                        accept=".doc,.docx"
                                        className="d-none"
                                        onChange={(e) => handleVisaFormFileChange(idx, 'action2', e.target.files[0])}
                                      />
                                    </div>

                                    {wordFile && (
                                      <div className="d-flex align-items-center justify-content-between bg-light p-2 rounded">
                                        <div className="flex-grow-1">
                                          <small className="text-success d-block">
                                            <strong>✓ Uploaded:</strong>
                                          </small>
                                          <small className="text-muted d-block">
                                            {typeof wordFile === 'string' ? wordFile : wordFile.name}
                                          </small>
                                        </div>

                                        {wordUrl && (
                                          <div className="ms-2">
                                            <Button
                                              variant="outline-info"
                                              size="sm"
                                              onClick={() => openFileInNewTab(wordUrl)}
                                              title="View Document"
                                              className="d-flex align-items-center"
                                            >
                                              👁️ View
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex flex-column gap-1">
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => {
                                        const newItems = [...visaFormItems];
                                        newItems.splice(idx, 1);
                                        setVisaFormItems(newItems);
                                      }}
                                      title="Remove"
                                    >
                                      <Trash size={14} />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}

                    <Card className="mt-3">
                      <Card.Body>
                        <Form.Group>
                          <Form.Label>Tourist Visa Remarks</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={touristVisaRemarks}
                            onChange={handleTouristVisaRemarksChange}
                            placeholder="Enter remarks about visa forms..."
                          />
                        </Form.Group>
                      </Card.Body>
                    </Card>
                  </Tab>

                  {/* Subtab 5: Photo */}
                  <Tab eventKey="photo" title="Photo">
                    <Card className="mb-4">
                      <Card.Body>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {editingType === 'photo' ? 'Edit Photo Description' : 'Add Photo Description'}
                            {editingType === 'photo' && (
                              <span className="badge bg-warning text-dark ms-2">
                                Editing item #{editIndex + 1}
                              </span>
                            )}
                          </Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Control
                              as="textarea"
                              rows={2}
                              name="description"
                              value={photoForm.description}
                              onChange={handlePhotoChange}
                              placeholder="Type photo requirement description"
                            />
                          </div>
                        </Form.Group>
                      </Card.Body>
                    </Card>

                    {photoItems.length > 0 && (
                      <Card>
                        <Card.Body>
                          <Table striped bordered hover size="sm">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Description</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {photoItems.map((item, idx) => (
                                <tr key={idx} className={editingType === 'photo' && editIndex === idx ? 'table-warning' : ''}>
                                  <td>{idx + 1}</td>
                                  <td>{item.description || '-'}</td>
                                  <td>
                                    <div className="d-flex gap-1">
                                      <Button
                                        variant={editingType === 'photo' && editIndex === idx ? "warning" : "outline-warning"}
                                        size="sm"
                                        onClick={() => editPhoto(idx)}
                                        title="Edit"
                                      >
                                        <Pencil size={14} />
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => removePhoto(idx)}
                                        title="Remove"
                                      >
                                        <Trash size={14} />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    )}
                  </Tab>

                  {/* Subtab 6: Visa Fees */}
                  <Tab eventKey="fees" title="Visa Fees">
                    <div className="mb-3">
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={addVisaFeesRow}
                      >
                        + Add New Visa Fees
                      </Button>
                    </div>

                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Tourist Visa</th>
                          <th>Toursit Visa Charges</th>
                          <th>Transit Visa</th>
                          <th>Transit Visa Charges</th>
                          <th>Business Visa</th>
                          <th>Business Visa Charges</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visaFeesRows.map((row) => (
                          <tr key={row.id}>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.tourist}
                                onChange={(e) => handleVisaFeesChange(row.id, 'tourist', e.target.value)}
                                placeholder="Free flow entry"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.tourist_charges}
                                onChange={(e) => handleVisaFeesChange(row.id, 'tourist_charges', e.target.value)}
                                placeholder="Charges for Tourist"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.transit}
                                onChange={(e) => handleVisaFeesChange(row.id, 'transit', e.target.value)}
                                placeholder="Free flow entry"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.transit_charges}
                                onChange={(e) => handleVisaFeesChange(row.id, 'transit_charges', e.target.value)}
                                placeholder="Charges for Transit"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.business}
                                onChange={(e) => handleVisaFeesChange(row.id, 'business', e.target.value)}
                                placeholder="Free flow entry"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.business_charges}
                                onChange={(e) => handleVisaFeesChange(row.id, 'business_charges', e.target.value)}
                                placeholder="Charges for Business"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeVisaFeesRow(row.id)}
                                title="Remove"
                                disabled={row.id <= 3}
                              >
                                <Trash size={14} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Tab>

                  {/* Subtab 7: Submission & Pick Up */}
                  <Tab eventKey="submission" title="Submission & Pick Up">
                    <div className="mb-3">
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={addSubmissionRow}
                      >
                        + Add New Submission & Pick Up
                      </Button>
                    </div>

                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th width="25%">Item</th>
                          <th width="25%">Tourist Visa</th>
                          <th width="25%">Transit Visa</th>
                          <th width="25%">Business Visa</th>
                          <th width="5%">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {submissionRows.map((row) => (
                          <tr key={row.id}>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.label}
                                onChange={(e) => handleSubmissionLabelChange(row.id, e.target.value)}
                                placeholder="Enter item label"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.tourist}
                                onChange={(e) => handleSubmissionValueChange(row.id, 'tourist', e.target.value)}
                                placeholder="Free flow alphanumeric"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.transit}
                                onChange={(e) => handleSubmissionValueChange(row.id, 'transit', e.target.value)}
                                placeholder="Free flow alphanumeric"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.business}
                                onChange={(e) => handleSubmissionValueChange(row.id, 'business', e.target.value)}
                                placeholder="Free flow alphanumeric"
                                size="sm"
                              />
                            </td>
                            <td>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeSubmissionRow(row.id)}
                                title="Remove"
                              >
                                <Trash size={14} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Tab>
                </Tabs>
              </Tab>

              <Tab eventKey="bookingPoi" title="Booking POI">
                <Form.Group className="mb-3">
                  <Row>
                    <Col md={8}>
                      <Form.Label>Booking Policy</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={poiText}
                        onChange={(e) => setPoiText(e.target.value)}
                        placeholder="Type booking policy here"
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label>Amount Details</Form.Label>
                      <Form.Control
                        type="text"
                        value={poiAmount}
                        onChange={(e) => setPoiAmount(e.target.value)}
                        placeholder="Enter amount details"
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mt-3">
                    <Form.Label>Booking Policy Remarks</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="booking_poi_remarks"
                      value={formData.booking_poi_remarks}
                      onChange={handleBasicChange}
                    />
                  </Form.Group>
                </Form.Group>

                {bookingPois.length > 0 && (
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item</th>
                        <th>Amount Details</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookingPois.map((p, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td style={{ whiteSpace: 'pre-line' }}>{p.item}</td>
                          <td>{p.amount_details}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => editPoi(idx)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removePoi(idx)}
                                title="Remove"
                              >
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              <Tab eventKey="cancellation" title="Cancellation Policy">
                <Row>
                  <Col md={8}>
                    <Form.Label>Cancellation Policy</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="cancellation_policy"
                      value={cancelItem.cancellation_policy}
                      onChange={handleCancelChange}
                      placeholder="Type cancellation policy here"
                      style={{ whiteSpace: 'pre-line' }}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Label>Charges</Form.Label>
                    <Form.Control
                      type="text"
                      name="charges"
                      value={cancelItem.charges}
                      onChange={handleCancelChange}
                      placeholder="Example: No refund / 50% retained"
                    />
                  </Col>
                </Row>

                <Form.Group className="mt-3">
                  <Form.Label>Cancellation Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="cancellation_remarks"
                    value={formData.cancellation_remarks}
                    onChange={handleBasicChange}
                  />
                </Form.Group>

                {cancelPolicies.length > 0 && (
                  <Table striped bordered hover className="mt-3" size="sm">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Cancellation Policy</th>
                        <th>Charges</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cancelPolicies.map((c, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td style={{ whiteSpace: 'pre-line', maxWidth: '400px' }}>
                            <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                              {c.cancellation_policy}
                            </div>
                          </td>
                          <td>{c.charges || "-"}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => editCancelRow(idx)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeCancelRow(idx)}
                                title="Remove"
                              >
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              <Tab eventKey="instructions" title="Instructions">
                <Form.Group className="mb-3">
                  <Form.Label>Instruction</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={instructionText}
                    onChange={(e) => setInstructionText(e.target.value)}
                    placeholder="Type instruction"
                  />
                </Form.Group>

                {instructions.length > 0 && (
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Instruction</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {instructions.map((item, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{item}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => editInstruction(idx)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeInstruction(idx)}
                                title="Remove"
                              >
                                <Trash size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              <Tab eventKey="images" title="Images">
                <Card className="mb-4">
                  <Card.Header>Add New Images</Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Upload New Images</Form.Label>
                      <Form.Control
                        type="file"
                        multiple
                        onChange={handleImageChange}
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                      />
                      <Form.Text className="text-muted">
                        You can select multiple images (JPEG, PNG, WebP). Max 5MB per image.
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Caption (optional - applies to all new images)</Form.Label>
                      <Form.Control
                        type="text"
                        value={imageCaption}
                        onChange={(e) => setImageCaption(e.target.value)}
                        placeholder="Enter a caption for the new images"
                      />
                    </Form.Group>

                    {imagePreviews.length > 0 && (
                      <div className="mt-3">
                        <p className="mb-2">
                          <strong>{imagePreviews.length} new image(s) ready to upload:</strong>
                        </p>
                        <Row>
                          {imageFiles.map((file, idx) => (
                            <Col md={3} key={idx} className="mb-3">
                              <div className="position-relative">
                                <img
                                  src={imagePreviews[idx]}
                                  alt={`new-${idx}`}
                                  style={{
                                    width: '100%',
                                    height: '150px',
                                    objectFit: 'cover',
                                    borderRadius: '8px'
                                  }}
                                />
                                <div className="position-absolute top-0 end-0 bg-dark bg-opacity-50 text-white p-1 rounded">
                                  {file.name}
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header>Existing Images</Card.Header>
                  <Card.Body>
                    {existingImages.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-muted">No images uploaded yet.</p>
                      </div>
                    ) : (
                      <Row>
                        {existingImages.map((image) => (
                          <Col md={4} lg={3} key={image.image_id} className="mb-4">
                            <Card className="h-100">
                              <Card.Body className="p-2">
                                <div className="position-relative">
                                  <img
                                    src={image.url}
                                    alt={`tour-image-${image.image_id}`}
                                    style={{
                                      width: '100%',
                                      height: '150px',
                                      objectFit: 'cover',
                                      borderRadius: '6px'
                                    }}
                                    className="mb-2"
                                  />

                                  {image.is_cover === 1 && (
                                    <div className="position-absolute top-0 start-0 bg-warning text-dark px-2 py-1 rounded-end">
                                      <strong>★ Cover</strong>
                                    </div>
                                  )}

                                  {editingImageId === image.image_id ? (
                                    <div className="mt-3 border p-3 rounded">
                                      <Form.Group>
                                        <Form.Label>Replace with new image:</Form.Label>
                                        <Form.Control
                                          id="replacementFileInput"
                                          type="file"
                                          onChange={handleReplacementFileChange}
                                          accept="image/jpeg,image/jpg,image/png,image/webp"
                                        />
                                      </Form.Group>

                                      {replacementPreview && (
                                        <div className="mt-2">
                                          <p><strong>New preview:</strong></p>
                                          <img
                                            src={replacementPreview}
                                            alt="replacement"
                                            style={{
                                              width: '100%',
                                              height: '100px',
                                              objectFit: 'cover',
                                              borderRadius: '4px'
                                            }}
                                          />
                                        </div>
                                      )}

                                      <div className="d-flex gap-2 mt-3">
                                        <Button
                                          variant="success"
                                          size="sm"
                                          onClick={() => updateImage(image.image_id)}
                                          disabled={!replacementFile || loading}
                                        >
                                          {loading ? 'Updating...' : 'Update'}
                                        </Button>
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          onClick={cancelEditImage}
                                          disabled={loading}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="mt-2">
                                      <div className="d-flex flex-wrap gap-1 justify-content-center">
                                        {image.is_cover === 0 && (
                                          <Button
                                            variant="outline-warning"
                                            size="sm"
                                            onClick={() => setCoverImage(image.image_id)}
                                            title="Set as Cover"
                                            disabled={loading}
                                          >
                                            ★ Set Cover
                                          </Button>
                                        )}

                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          onClick={() => startEditImage(image)}
                                          title="Replace Image"
                                          disabled={loading}
                                        >
                                          <Pencil size={14} /> Replace
                                        </Button>

                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          onClick={() => deleteImage(image.image_id)}
                                          title="Delete Image"
                                          disabled={loading}
                                        >
                                          <Trash size={14} />
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </Card.Body>
                              <Card.Footer className="bg-transparent border-0 pt-0">
                                <small className="text-muted">
                                  {image.caption ? `Caption: ${image.caption}` : 'No caption'}
                                </small>
                              </Card.Footer>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    )}

                    {existingImages.length > 0 && (
                      <div className="mt-3 text-center">
                        <p className="text-muted">
                          Total images: {existingImages.length} |
                          Cover image: {existingImages.find(img => img.is_cover === 1) ? 'Set' : 'Not set'}
                        </p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </Container>
    </Navbar>
  );
};

export default AddHoneyMoonTour;